/**
 * The entry repository: every read and write of the device database.
 *
 * The mirror is the source of truth for everything on screen; the network is
 * replication. This module and db.ts are the only places SQL RUNS. Two pure
 * modules BUILD it — insight-views.ts the per-record view DDL, insight-query.ts
 * the insight aggregates — and this file is where they meet a connection.
 * Screens and hooks call these functions and never see a statement.
 *
 * Queries go through Drizzle's query builder over the tables in db-schema.ts.
 * The expo-sqlite driver is synchronous, so a "transaction" here is
 * `db.transaction` with a plain callback: same BEGIN/COMMIT, no interleaving
 * hazard, and the two inserts of a pending save commit or roll back together.
 */
import type {
  EntryLinesWrite,
  EntryListItem,
  EntryValues,
  LineWrite,
  RecordGrantSummary,
  RecordListItem,
  RecordListToday,
  RecordSchemaResponse,
  SyncBucketRow,
  WorkspaceBoardCard,
} from '@alluzo/shared';
import { aggLeavesOf, cardRecordIds, isRecordIconId, linesOf, LINES_MAX_DEPTH, REGISTRY_VERSION } from '@alluzo/shared';
import { and, asc, desc, eq, inArray, isNull, like, sql, type SQL } from 'drizzle-orm';
import type { AnySQLiteColumn } from 'drizzle-orm/sqlite-core';

import { execRaw, execRawAsync, getDb, queryRaw, queryRawAsync, runRaw, withPrepared, type DeviceDb } from '@/lib/db';

import {
  arrayFieldsOf,
  compileEntryFilter,
  compileEntryFilterSummary,
  compileEntryPageBoundary,
  isFiltering,
  type EntryFilterSet,
} from '@/lib/entry-filter';
import {
  aggregateBucket,
  businessInsightLayout,
  entryMirror,
  insightLayout,
  outbox,
  pickTitle,
  recordCatalog,
  schemaCache,
  syncMeta,
} from '@/lib/db-schema';
import { fillFormulas, fillFormulaTree, holdRules, withoutComputed, withoutComputedLines } from '@/lib/formula-fill';
import { lineRefFields, withoutLineRefs } from '@/lib/line-above';
import { doomedStatement, floorStatement, isPrunable, laterFloor, pointedRecords, PRUNE_CHUNK } from '@/lib/mirror-window';
import { dayOf, fieldFor, measureIsMovement, measureOf, signOf, titleOf } from '@/lib/entry-format';
import { referenceIdsOf } from '@/lib/entry-row';
import { packGrant, rereadReason, rowsKept, unpackGrant } from '@/lib/record-grant';
import {
  FORGOTTEN_SCHEMA_VERSION,
  givenRecords,
  noteShift,
  readRereadState,
  schemaAskOf,
  sendableSchemaVersions,
  settleSeen,
  writeRereadState,
  type RereadState,
} from '@/lib/reread';
import { docWithLines, isLinesSchema, linesUnder, rootOfLine } from '@/lib/line-search-doc';
import { refToken, type SearchDocument } from '@/lib/search-doc';
import { useSetupDraftStore } from '@/stores/setup-draft-store';
import {
  clearRowsSql,
  clearWordsSql,
  deleteDocRowSql,
  deleteDocsRowsSql,
  deleteDocsWordsSql,
  deleteDocWordsSql,
  deleteRecordRowsSql,
  deleteRecordWordsSql,
  deleteOrphanRowsSql,
  deleteOrphanWordsSql,
  docCountSql,
  insertDocSql,
  liveCountSql,
  linkDocSql,
  needsIndexSql,
  REBUILD_CHUNK,
  rebuildIdsFirstWriterSql,
  rebuildIdsNextWriterSql,
  rebuildIdsSameWriterSql,
  rebuildRowsSql,
  linesUnderSql,
  rootPathOf,
  recordEntriesSql,
  containsPattern,
  eitherExpression,
  FALLBACK_SCAN_LIMIT,
  isSearchable,
  isSearching,
  looseExpression,
  looseSql,
  matchCountSql,
  matchExpression,
  nearest,
  recordLiveCountSql,
  recordMatchSql,
  REF_SEED_LIMIT,
  refsMatchExpression,
  RESULT_LIMIT,
  searchSql,
  seedSql,
  titleMatchExpression,
} from '@/lib/search-query';
import {
  parseBusinessLayout,
  type BusinessInsightLayoutEntry,
} from '@/lib/insight-business-layout';
import {
  compileBusinessInsight,
  compileBusinessInsightAtHorizon,
  isHostedSpec,
  isLedgerTileSpec,
  type BusinessContext,
  type BusinessInsightSpec,
  type BusinessOwnSpec,
} from '@/lib/insight-business-query';
import { forgetAnswerAsks } from '@/lib/insight-answers';
import { parseLayout, type InsightLayoutEntry } from '@/lib/insight-layout';
import {
  compileInsight,
  compileInsightAtHorizon,
  cutBreakdown,
  fillSeries,
  fillSeriesRange,
  GROUP_SCAN_LIMIT,
  groupsOverlap,
  overlapTotalSpec,
  readPair,
  readScalar,
  type GroupedRow,
  type InsightCompileContext,
  type InsightResult,
  type InsightSpec,
  type PairRow,
  type RuntimeCut,
  type ScalarRow,
} from '@/lib/insight-query';
import { dropView, fieldIndexChanges, fieldIndexPrefixFor, rebuildView } from '@/lib/insight-views';
import { newLocalId } from '@/lib/local-id';
import type { SyncEntryRow, SyncPickTitle } from '@/lib/sync-types';
import { countedOnce, pickTodayCurrency, toTodayTotals, utcToday, workspaceToday } from '@/lib/today-totals';

/**
 * The handle inside `getDb().transaction(...)`. Drizzle does not export it by
 * name, so it is derived from the callback rather than restated — a restated
 * copy would drift the first time the driver changes shape.
 */
type DeviceTx = Parameters<Parameters<DeviceDb['transaction']>[0]>[0];

/** One row as the entry list draws it: the server shape, plus the pending flag. */
export type EntryListRow = EntryListItem & {
  pending?: boolean;
  /**
   * Fields whose figure holds an entry the server has not saved yet: a room
   * bill with an order still in the outbox, and the totals that read it
   * (`withWaitingRollups`). Absent on every other row.
   */
  unsaved?: readonly string[];
};

/** A row this device wrote and the server has not accepted yet. */
export type PendingRow = EntryListRow & { pending: true };

/** One outbox op, values parsed. */
export type OutboxOp = {
  opId: string;
  kind: string;
  recordId: string;
  entryId: string;
  values: EntryValues;
  /**
   * The lines the write carries, at both levels (Decision 5 of
   * docs/plans/entry-lines-and-layout.md): typed values only, no answer and
   * no parent or root. Absent when the op names none.
   */
  lines?: EntryLinesWrite;
  fieldSetVer: number | null;
  createdAt: string;
};

type EntryMirrorRow = typeof entryMirror.$inferSelect;

const toListRow = (row: EntryMirrorRow): EntryListRow => ({
  entryId: row.entryId,
  version: row.version,
  values: JSON.parse(row.valuesJson) as EntryValues,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  createdBy: row.createdBy ?? 'local',
  sourceChannel: row.sourceChannel ?? 'typed',
  linkId: row.linkId ?? null,
  sourceEntryId: row.sourceEntryId ?? null,
  ...(row.pending ? { pending: true as const } : {}),
});

/**
 * The two columns the mirror materializes at write time, computed by the same
 * pure functions the UI uses, so a row lists, sums, and groups identically
 * whether it arrived by pull or by local save. Null schema (a pull page that
 * named a record whose schema never landed) materializes nothing rather than
 * guessing.
 */
const materialize = (
  schema: RecordSchemaResponse | null,
  values: EntryValues,
  createdAt: string,
): { day: string | null; signedValue: number | null } => {
  if (schema === null) return { day: null, signedValue: null };
  const probe: EntryListItem = {
    entryId: '',
    version: 0,
    values,
    createdAt,
    updatedAt: createdAt,
    createdBy: '',
    sourceChannel: '',
    linkId: null,
    sourceEntryId: null,
  };
  const measure = measureOf(schema, values);
  const sign = signOf(schema, values);
  return {
    day: dayOf(schema, probe),
    signedValue: measure !== null && sign !== null ? measure * sign : null,
  };
};

/* ---------------------------------------------------------- tenant scope */

/*
 * One phone, several businesses.
 *
 * Three tables carry a `tenant_id` — the mirror, the outbox and the catalog —
 * because those are the three something scans without naming a record.
 * Everything else is keyed by record id, and a record id is a uuid, unique
 * across businesses, so a keyed read cannot bleed. Where one of those tables
 * must be read business-wide it is scoped THROUGH the catalog:
 * `record_id IN (SELECT id FROM record_catalog WHERE tenant_id = ?)`.
 *
 * **Why every tenant argument is LAST and OPTIONAL.** The screens, hooks and
 * sync engine that call this file are being rewired to pass the active business
 * by another hand; a required first parameter would break every one of them at
 * once. So each scoped function takes `tenantId` last, and works out what a
 * caller that says nothing must have meant.
 *
 * **What "said nothing" means, and why there are two answers.** A screen means
 * the business the person is looking at: the stored pick, falling back to the
 * only business on the phone. A SYNC path means the business the pull or push
 * in flight is for, which is NOT the stored pick — `switchTo` fires a parting
 * `sync(previous)` on purpose, so at that moment the stored pick is the new
 * business and the sync running is the old one's. Guessing the pick there would
 * file one business's records under another's name, or prune them. So a sync path
 * falls back to the only business on the phone and to nothing else, and the two
 * paths where a wrong guess destroys data — `listOutbox` and `pruneCatalog` —
 * refuse to act at all rather than act on a guess.
 *
 * On every device that exists today there is exactly one business on the phone,
 * so every one of these fallbacks answers the same thing the un-scoped code
 * answered, and this whole section is invisible until the switcher ships.
 */

/** The key holding which business this device is looking at. */
const ACTIVE_TENANT_KEY = 'active_tenant';

/**
 * The business this device is looking at, or null when nobody has chosen.
 *
 * In the device database and not AsyncStorage, so `forgetDevice()` wipes it
 * with everything else — the next account on this phone can never inherit the
 * last one's pick.
 */
export const getActiveTenant = (): string | null => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, ACTIVE_TENANT_KEY))
    .get();
  return row === undefined ? null : row.value;
};

/** Remember the pick. Written on the tap, so a crash cannot undo a switch. */
export const setActiveTenant = (tenantId: string): void => {
  getDb()
    .insert(syncMeta)
    .values({ key: ACTIVE_TENANT_KEY, value: tenantId })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value: tenantId } })
    .run();
};

/**
 * Every business this device holds data for, read from the pull cursors.
 *
 * The cursors are the honest register of it: `since:<tenantId>` is written by
 * the first pull a business ever gets on this phone and outlives every prune.
 * The same fact migration 6's backfill reads, read the same way.
 */
const tenantsHeld = (): string[] =>
  getDb()
    .select({ key: syncMeta.key })
    .from(syncMeta)
    .where(like(syncMeta.key, 'since:%'))
    .all()
    .map((row) => row.key.slice('since:'.length))
    .filter((tenantId) => tenantId !== '');

/**
 * The one business on this phone, or null when there are none or several.
 *
 * Null on "several" is the whole point: with two businesses here, a caller that
 * did not say which one it meant cannot be guessed for.
 */
const soleTenant = (): string | null => {
  const held = tenantsHeld();
  return held.length === 1 ? (held[0] ?? null) : null;
};

/** What a SCREEN means by saying nothing: the pick, else the only one here. */
const shownTenant = (tenantId?: string | null): string | null =>
  tenantId ?? getActiveTenant() ?? soleTenant();

/**
 * What a SYNC path means by saying nothing, and whether guessing is safe.
 *
 * Never the stored pick — see the note at the top of this section. `ambiguous`
 * is true when this phone holds more than one business and the caller named
 * none, which is the state where a destructive path must do nothing.
 */
const syncScope = (
  tenantId?: string | null,
): { readonly tenant: string | null; readonly ambiguous: boolean } => {
  if (tenantId !== undefined && tenantId !== null) return { tenant: tenantId, ambiguous: false };
  const held = tenantsHeld();
  return { tenant: held.length === 1 ? (held[0] ?? null) : null, ambiguous: held.length > 1 };
};

/**
 * `tenant_id = ?`, or nothing at all when no business could be named.
 *
 * Nothing at all — rather than `tenant_id IS NULL` — because the only device
 * that cannot name one is a device that has never synced, whose tables are
 * empty, and because a predicate matching nothing would draw an empty screen
 * where the un-scoped code drew the truth. Drizzle's `and` drops an undefined.
 */
const sameTenant = (column: AnySQLiteColumn, tenantId: string | null): SQL | undefined =>
  tenantId === null ? undefined : eq(column, tenantId);

/**
 * `record_id IN (SELECT id FROM record_catalog WHERE tenant_id = ?)`, for the
 * record-keyed tables that carry no tenant of their own.
 */
const inTenantRecords = (column: AnySQLiteColumn, tenantId: string | null): SQL | undefined =>
  tenantId === null
    ? undefined
    : inArray(
        column,
        getDb()
          .select({ id: recordCatalog.id })
          .from(recordCatalog)
          .where(eq(recordCatalog.tenantId, tenantId)),
      );

/** Which business a record belongs to, for a write path holding only its id. */
const tenantOfRecord = (recordId: string): string | null => {
  const row = getDb()
    .select({ tenantId: recordCatalog.tenantId })
    .from(recordCatalog)
    .where(eq(recordCatalog.id, recordId))
    .get();
  return row?.tenantId ?? null;
};

/* ------------------------------------------------------------------ reads */

/**
 * One record's live rows, newest day first, newest sequence first within a
 * day — the server's own list order. Pending rows carry no server_seq and
 * sink to the bottom of their day; createdAt breaks their tie.
 */
export const listEntries = (recordId: string): EntryListRow[] =>
  getDb()
    .select()
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.recordId, recordId),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .orderBy(desc(entryMirror.day), desc(entryMirror.serverSeq), desc(entryMirror.createdAt))
    .all()
    .map(toListRow);

/**
 * One record's live rows for ONE day, newest first.
 *
 * The sheet needs a day and nothing else: eight marks out of a year of them.
 * `listEntries` would read every row of the record and cut it in JavaScript,
 * which is a scan of thousands to draw eight, so this cuts in SQL instead — and
 * `entry_mirror_list`, on `(record_id, day DESC) WHERE deleted_at IS NULL`, is
 * already the index for it.
 *
 * `day` is the materialized column, the same one `groupByDay` reads back out of
 * the row, so a mark filed under a backdated date is on the day the person
 * chose rather than the day they typed it.
 */
export const entriesOnDay = (recordId: string, day: string): EntryListRow[] =>
  getDb()
    .select()
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.recordId, recordId),
        eq(entryMirror.day, day),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .orderBy(desc(entryMirror.serverSeq), desc(entryMirror.createdAt))
    .all()
    .map(toListRow);

/**
 * The values of one mirrored row, or null when this device does not hold it.
 *
 * The formula builder's preview reads the row a hop points at through this.
 */
export const valuesForEntry = (entryId: string): EntryValues | null => {
  const row = getDb()
    .select({ valuesJson: entryMirror.valuesJson })
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.entryId, entryId),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .limit(1)
    .all()[0];
  if (row === undefined) return null;
  try {
    return JSON.parse(row.valuesJson) as EntryValues;
  } catch {
    return null;
  }
};

/**
 * The newest live row on one record, or null when the record is empty.
 *
 * The formula builder tries a draft on it, so the answer on screen is one this
 * business has actually seen rather than a made-up number.
 */
export const newestEntryFor = (recordId: string): EntryListRow | null =>
  getDb()
    .select()
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.recordId, recordId),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .orderBy(desc(entryMirror.day), desc(entryMirror.serverSeq), desc(entryMirror.createdAt))
    .limit(1)
    .all()
    .map(toListRow)[0] ?? null;

/**
 * One row of the filtered statement, already aliased by the compiler.
 *
 * The alias list in `entry-filter.ts` and this type must change together: the
 * whole reason the columns are aliased is that the row then maps onto
 * `EntryListRow` without a second naming convention in between.
 */
type FilteredRow = {
  entryId: string;
  version: number;
  valuesJson: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  sourceChannel: string | null;
  linkId: string | null;
  sourceEntryId: string | null;
  pending: number;
};

/**
 * One record's live rows under a filter, in exactly `listEntries`' order.
 *
 * An empty filter answers `listEntries` itself rather than compiling a
 * statement with no conditions. That is not an optimisation — it is what keeps
 * the unfiltered screen on the code path it has always been on, so turning the
 * feature on cannot change what an unfiltered list draws.
 */
export const listEntriesFiltered = (
  recordId: string,
  set: EntryFilterSet,
  today: string = workspaceToday(),
  text: string | null = null,
): EntryListRow[] => {
  if (!isFiltering(set) && !isSearching(text)) return listEntries(recordId);
  const { sql: statement, params } = compileEntryFilter(recordId, set, today, arrayFieldsOf(schemaFor(recordId)), text);
  return queryRaw<FilteredRow>(statement, [...params]).map(fromFilteredRow);
};

const fromFilteredRow = (row: FilteredRow): EntryListRow => ({
  entryId: row.entryId,
  version: row.version,
  values: JSON.parse(row.valuesJson) as EntryValues,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  createdBy: row.createdBy ?? 'local',
  sourceChannel: row.sourceChannel ?? 'typed',
  linkId: row.linkId ?? null,
  sourceEntryId: row.sourceEntryId ?? null,
  ...(row.pending ? { pending: true as const } : {}),
});

/**
 * One live row of one record, as the list would draw it, or none.
 *
 * What the edit screen reads instead of the whole record: it opens one entry,
 * and finding that entry by reading two lakh others was most of what made the
 * screen slow to open.
 */
export const liveEntryRow = (recordId: string, entryId: string): EntryListRow[] =>
  getDb()
    .select()
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.recordId, recordId),
        eq(entryMirror.entryId, entryId),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .limit(1)
    .all()
    .map(toListRow);

/**
 * The first `size` rows of the list, or a little more, in `listEntries`' order.
 *
 * What the record screen reads. A record of two lakh rows used to be read and
 * parsed whole on the JavaScript thread every time it opened, which froze the
 * phone for over a minute; a person reads the top of the list, so the top is
 * what is read, and the screen asks for more as it scrolls.
 *
 * A PAGE ENDS ON A WHOLE DAY. The rows are cut at the day of the `size`th row
 * and every row of that day is kept, so a day row on a sheet record never
 * counts half its marks. Past the rows that carry a day, the cut is a plain
 * limit, because there is no day left to split.
 *
 * `text` cuts the page to the record's own words, as it cuts
 * `listEntriesFiltered`, and it goes last for the reason `filteredSummary`
 * gives. The boundary is counted under the same words, so a search reads a
 * page of its matches rather than every one of them.
 *
 * `listEntriesFiltered` stays for the callers that do need everything.
 */
export const listEntriesPage = (
  recordId: string,
  set: EntryFilterSet,
  size: number,
  today: string = workspaceToday(),
  wholeDays = true,
  text: string | null = null,
): EntryListRow[] => {
  // A list of entries can end anywhere: it draws no per-day total. Only a
  // sheet record, whose row IS a day, needs its page to end on a whole day —
  // and a busy site's whole day is four hundred rows, which is what the first
  // page of every record used to be.
  //
  // The day cut stays even when the page may end inside a day, and the limit
  // goes on top of it. Every one of the first `size` rows is on or after the
  // day of the `size`th, so the rows are the same. What the cut buys is the
  // plan: without a day range, `ORDER BY day DESC, server_seq DESC, ...` with a
  // LIMIT sorted every row of the record, `values_json` and all, to keep sixty.
  // On a record of 2.7 lakh rows that was 18 to 21 s on the Android emulator;
  // with the range it is a few milliseconds.
  const boundary = compileEntryPageBoundary(recordId, set, today, size, arrayFieldsOf(schemaFor(recordId)), text);
  const cutAt = queryRaw<{ day: string | null }>(boundary.sql, [...boundary.params])[0];
  const cut =
    cutAt === undefined
      ? {}
      : cutAt.day === null
        ? { limit: size }
        : wholeDays
          ? { dayAtLeast: cutAt.day }
          : { dayAtLeast: cutAt.day, limit: size };
  const { sql: statement, params } = compileEntryFilter(recordId, set, today, arrayFieldsOf(schemaFor(recordId)), text, cut);
  return queryRaw<FilteredRow>(statement, [...params]).map(fromFilteredRow);
};

/**
 * How many rows the filter kept, and what they come to.
 *
 * Run as its own statement rather than summed from the rows above, for two
 * reasons: the sheet's button needs the count BEFORE the list is fetched, and
 * `signed_value` is a column the row map does not carry.
 *
 * `net` is null when nothing matched, and also when everything that matched is
 * unsigned — a record that measures nothing has no total, and drawing a nought
 * for it would invent one.
 *
 * **`text` goes LAST, and that is not tidiness.** `today` is the third
 * parameter and `use-entries.ts` passes it positionally. A `text` slipped in
 * before it would have handed a date string to FTS5 as a match expression —
 * which compiles, runs, and quietly answers nothing, with no type error and no
 * failing test anywhere to say so.
 */
export const filteredSummary = (
  recordId: string,
  set: EntryFilterSet,
  today: string = workspaceToday(),
  text: string | null = null,
): { count: number; net: number | null } => {
  const schema = schemaFor(recordId);
  const movement = schema !== null && measureIsMovement(schema) ? fieldFor(schema, 'measure') : null;
  const { sql: statement, params } = compileEntryFilterSummary(recordId, set, today, arrayFieldsOf(schema), text, movement);
  const row = queryRaw<{ n: number; net: number | null }>(statement, [...params])[0];
  return { count: row?.n ?? 0, net: row?.net ?? null };
};

/**
 * How many live rows the whole record holds, whatever the filter says.
 *
 * The filtered-empty state needs it. "Add your first sale" put in front of
 * somebody whose filter matched nothing would be a lie about a record with four
 * hundred sales in it, so the screen says how many are really there.
 */
export const countEntries = (recordId: string): number => {
  const row = getDb()
    .select({ n: sql<number>`count(*)` })
    .from(entryMirror)
    .where(
      and(
        eq(entryMirror.recordId, recordId),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .get();
  return row?.n ?? 0;
};

/**
 * How many live rows the record holds, counting no further than `cap`.
 *
 * What the record screen asks as it opens, in place of `countEntries`. The
 * screen needs to know whether more rows follow its page and whether the
 * record is empty, and both are answered a row past the page. Counting every
 * row took 2.3 s on the Android emulator for a record of 2.7 lakh rows, every
 * time the record opened. This walks `entry_mirror_record_live` and stops at
 * `cap`, so it costs the page, not the record.
 */
export const countEntriesUpTo = (recordId: string, cap: number): number =>
  queryRaw<{ n: number }>(
    `SELECT count(*) AS n FROM (
       SELECT 1 FROM entry_mirror
        WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL
        LIMIT ?)`,
    [recordId, cap],
  )[0]?.n ?? 0;

/**
 * How many entries the server says the record holds, from the catalogue.
 *
 * The number the Records list already shows. It leaves out rows saved on this
 * phone that have not synced yet, so it is for words on the screen only, never
 * for deciding what the list holds.
 */
export const catalogEntryCount = (recordId: string): number =>
  getDb()
    .select({ n: recordCatalog.entryCount })
    .from(recordCatalog)
    .where(eq(recordCatalog.id, recordId))
    .get()?.n ?? 0;

/** The cached schema for one record, or null when no pull or fetch stored one. */
export const schemaFor = (recordId: string): RecordSchemaResponse | null => {
  const row = getDb()
    .select()
    .from(schemaCache)
    .where(eq(schemaCache.recordId, recordId))
    .get();
  return row === undefined ? null : (JSON.parse(row.schemaJson) as RecordSchemaResponse);
};

/**
 * recordId -> field_set_ver for every cached schema. The pull request's half.
 *
 * Scoped through the catalog, because this map is SENT: a pull for one business
 * naming another's records would be asking a server about rows outside the
 * business it named, and the answer to that is at best a refusal.
 *
 * A schema whose catalog row has not landed yet — `cacheSchemas` runs before
 * `upsertCatalog` on a first pull — is left out of a scoped answer and re-sent
 * by the server next page, which costs a few bytes once. An UNSCOPED answer
 * still carries it, so nothing changes for a device with one business.
 */
export const schemaVersions = (tenantId?: string | null): Record<string, number> =>
  // A schema forgotten for a re-read is left out: the server refuses a
  // version under 1, and "not held" is what makes it send the schema again.
  sendableSchemaVersions(heldSchemaRows(tenantId));

const heldSchemaRows = (tenantId?: string | null): { recordId: string; fieldSetVer: number }[] =>
  getDb()
    .select({ recordId: schemaCache.recordId, fieldSetVer: schemaCache.fieldSetVer })
    .from(schemaCache)
    .where(inTenantRecords(schemaCache.recordId, syncScope(tenantId).tenant))
    .all();

/** Where the registry one business's cached schemas were served for is kept. */
const schemaRegistryKey = (tenantId: string): string => `schema_registry:${tenantId}`;

/**
 * The schema half of one business's pull request: the versions held, and the
 * registry this build draws with (`schemaAskOf`). Schemas cached for another
 * registry, or by a pull that named none, are named as none, so the server
 * sends every one again for this build.
 */
export const schemaAsk = (tenantId: string): { readonly schemaVersions: Record<string, number>; readonly registryVersion: number } => {
  const stored = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, schemaRegistryKey(tenantId)))
    .get();
  const servedAt = stored === undefined ? null : Number(stored.value);
  return schemaAskOf(heldSchemaRows(tenantId), Number.isInteger(servedAt) ? servedAt : null, REGISTRY_VERSION);
};

/**
 * A pull page asked with `schemaAsk` landed: this business's schemas are now
 * the ones served for this build's registry, every one of them, because a
 * pull that named none was sent all of them on its first page.
 */
export const markSchemasServed = (tenantId: string, registryVersion: number): void => {
  const value = String(registryVersion);
  getDb()
    .insert(syncMeta)
    .values({ key: schemaRegistryKey(tenantId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/**
 * The reference titles a row list needs, resolved offline.
 *
 * A reference value is a uuid; the server used to send the names behind them
 * as `references`. Here the target entry is already in the mirror, so its
 * title is computed with titleOf from the TARGET record's cached schema. One
 * level deep, as the server's map is: a title that is itself a reference
 * draws nothing.
 */
export const resolveReferences = (
  recordId: string,
  rows: readonly EntryListRow[],
): Record<string, string> => {
  const schema = schemaFor(recordId);
  if (schema === null) return {};
  // A multi select over a list holds row ids too, and its chips need names.
  const ids = referenceIdsOf(schema, rows);
  if (ids.size === 0) return {};
  return titlesForEntryIds([...ids]);
};

/**
 * The human name behind a set of entry ids, whatever record they live in.
 *
 * One level deep, as the server's own reference map is, and a title that is
 * itself a pick one step more, to what it picks. An id whose target is not mirrored, or whose
 * record has no cached schema, is simply absent from the answer — the caller
 * draws what it has.
 *
 * Shared by the entry list (which resolves the reference cells of its rows)
 * and by an insight breakdown (whose group keys are entry ids of a directory).
 */
export const titlesForEntryIds = (ids: readonly string[], depth = 0): Record<string, string> => {
  if (ids.length === 0) return {};

  const targets = getDb()
    .select({
      entryId: entryMirror.entryId,
      recordId: entryMirror.recordId,
      valuesJson: entryMirror.valuesJson,
    })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.entryId, [...ids]),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();

  const titles: Record<string, string> = {};
  const schemas = new Map<string, RecordSchemaResponse | null>();
  // An entry titled by a pick (a membership by its member, B3 of docs/plans/gym-membership.md) is named by
  // what it picks, one step on and no further, as the server names it (`resolveReferences`).
  const byPick: Array<{ entryId: string; schema: RecordSchemaResponse; values: EntryValues; picked: string }> = [];
  for (const target of targets) {
    let targetSchema = schemas.get(target.recordId);
    if (targetSchema === undefined) {
      targetSchema = schemaFor(target.recordId);
      schemas.set(target.recordId, targetSchema);
    }
    if (targetSchema === null) continue;
    const values = JSON.parse(target.valuesJson) as EntryValues;
    const titleField = fieldFor(targetSchema, 'title');
    const picked = titleField === null ? undefined : values[titleField];
    if (depth === 0 && typeof picked === 'string' && picked !== '' && targetSchema.fields.find((one) => one.id === titleField)?.type === 'reference') {
      byPick.push({ entryId: target.entryId, schema: targetSchema, values, picked });
      continue;
    }
    const title = titleOf(targetSchema, values, {});
    if (title !== '') titles[target.entryId] = title;
  }
  if (byPick.length > 0) {
    const named = titlesForEntryIds([...new Set(byPick.map((one) => one.picked))], depth + 1);
    for (const one of byPick) {
      const title = titleOf(one.schema, one.values, named);
      if (title !== '') titles[one.entryId] = title;
    }
  }
  // A picked entry the window left out (step 9.8): the title the pull sent for it. The mirror wins where it has the row.
  const mirrored = new Set(targets.map((target) => target.entryId));
  const missing = ids.filter((id) => !mirrored.has(id));
  if (missing.length > 0) {
    const kept = getDb().select({ entryId: pickTitle.entryId, title: pickTitle.title }).from(pickTitle).where(inArray(pickTitle.entryId, [...new Set(missing)])).all();
    for (const one of kept) if (one.title !== '') titles[one.entryId] = one.title;
  }
  return titles;
};

/** How many titles a server search showed are held for a save to keep. */
const SHOWN_TITLES_HELD = 500;

/**
 * The titles a server search showed in a picker, by entry id (Phase 9a review
 * of step 9.8). Held in memory only: a title is kept on the device
 * (`pick_title`) when a save here picks its entry, never for every row shown.
 */
const shownTitles = new Map<string, { readonly tenantId: string; readonly recordId: string; readonly title: string }>();

/** Hold the titles a server search showed for a picker over `recordId`, the newest over any held. */
export const noteShownTitles = (tenantId: string, recordId: string, options: readonly { readonly id: string; readonly title: string }[]): void => {
  for (const one of options) {
    if (one.title === '') continue;
    shownTitles.delete(one.id);
    shownTitles.set(one.id, { tenantId, recordId, title: one.title });
  }
  for (const oldest of shownTitles.keys()) {
    if (shownTitles.size <= SHOWN_TITLES_HELD) break;
    shownTitles.delete(oldest);
  }
};

/**
 * A save here that picks an entry a server search showed keeps its title
 * (`pick_title`), so the pending row, offline too, names what it picked until
 * a pull brings the row or its title. The mirror still wins where it holds it.
 */
const keepShownPicks = (tx: DeviceTx, schema: RecordSchemaResponse, values: EntryValues): void => {
  if (shownTitles.size === 0) return;
  for (const field of schema.fields) {
    if (field.targetRecordId === null || field.targetRecordId === undefined) continue;
    const value = values[field.id];
    const ids = typeof value === 'string' ? [value] : Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : [];
    for (const id of ids) {
      const shown = shownTitles.get(id);
      if (shown === undefined || shown.recordId !== field.targetRecordId) continue;
      tx.insert(pickTitle)
        .values({ entryId: id, tenantId: shown.tenantId, recordId: shown.recordId, title: shown.title })
        .onConflictDoUpdate({ target: pickTitle.entryId, set: { tenantId: shown.tenantId, recordId: shown.recordId, title: shown.title } })
        .run();
    }
  }
};

/**
 * Keep the titles a pull sent for picked entries the window leaves out
 * (step 9.8 of docs/plans/field-relations.md), each over the one held: the
 * newest pull names it as it is now.
 */
export const applyPickTitles = (titles: readonly SyncPickTitle[] | undefined, tenantId: string): void => {
  if (titles === undefined || titles.length === 0) return;
  getDb().transaction((tx) => {
    for (const one of titles) {
      tx.insert(pickTitle)
        .values({ entryId: one.entryId, tenantId, recordId: one.recordId, title: one.title })
        .onConflictDoUpdate({ target: pickTitle.entryId, set: { tenantId, recordId: one.recordId, title: one.title } })
        .run();
    }
  });
};

/** Where one entry lives, and what its own record calls it. */
export type EntrySource = {
  readonly recordId: string;
  /** The record's own name, for the mark a derived row wears. */
  readonly recordName: string;
  /** Its title, or '' when the record binds none it can be named by. */
  readonly title: string;
};

/**
 * The same read as `titlesForEntryIds`, keeping what that one throws away.
 *
 * A row a link wrote knows the id of the row it came from, and both halves of
 * this answer are needed before that means anything to a person: the record's
 * NAME is the word on the mark ("From Sales"), and the record's ID is how the
 * source row is opened. Neither needs a network, which is the point — a rule
 * is online only, so this is all a person has on a train.
 *
 * An entry that is not mirrored is absent from the answer, and that absence is
 * the whole of `link.provenance_unknown`: not an error, just a record this
 * device has never pulled.
 *
 * Kept beside `titlesForEntryIds` rather than folded into it, because that one
 * has callers that want a title and nothing else, and widening their answer to
 * serve this would make every entry list carry a shape it never reads.
 */
export const sourcesForEntryIds = (ids: readonly string[]): Record<string, EntrySource> => {
  if (ids.length === 0) return {};

  const targets = getDb()
    .select({
      entryId: entryMirror.entryId,
      recordId: entryMirror.recordId,
      valuesJson: entryMirror.valuesJson,
    })
    .from(entryMirror)
    .where(
      and(
        inArray(entryMirror.entryId, [...ids]),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
      ),
    )
    .all();

  const out: Record<string, EntrySource> = {};
  const schemas = new Map<string, RecordSchemaResponse | null>();
  for (const target of targets) {
    let targetSchema = schemas.get(target.recordId);
    if (targetSchema === undefined) {
      targetSchema = schemaFor(target.recordId);
      schemas.set(target.recordId, targetSchema);
    }
    // No cached schema means no name and no title. The row is still THERE, so
    // it is still openable — the screen it opens reads its own schema.
    const title =
      targetSchema === null
        ? ''
        : titleOf(targetSchema, JSON.parse(target.valuesJson) as EntryValues, {});
    out[target.entryId] = {
      recordId: target.recordId,
      recordName: targetSchema?.name ?? '',
      title,
    };
  }
  return out;
};

/**
 * What this device's person may do in one record, as the last pull left it.
 *
 * Null for an owner AND for a record this device has never pulled, which are the
 * same answer on purpose: `record-grant.ts` reads null as permissive, and the
 * note there says why. A screen must NOT read a missing record as "no
 * permissions" — that is a phone that has not synced, not a person who lost
 * their key.
 */
export const grantFor = (recordId: string): RecordGrantSummary | null => {
  const row = getDb()
    .select({
      grantCapabilities: recordCatalog.grantCapabilities,
      grantRowScope: recordCatalog.grantRowScope,
      grantWriteMode: recordCatalog.grantWriteMode,
      grantBalanceVisible: recordCatalog.grantBalanceVisible,
      grantDirection: recordCatalog.grantDirection,
      grantWritableFields: recordCatalog.grantWritableFields,
      grantReach: recordCatalog.grantReach,
      grantTeam: recordCatalog.grantTeam,
      grantMask: recordCatalog.grantMask,
    })
    .from(recordCatalog)
    .where(eq(recordCatalog.id, recordId))
    .get();
  return row === undefined ? null : unpackGrant(row);
};

/** The home list, in first-seen order (rowid), shaped as the server sent it. */
export const listCatalog = (tenantId?: string | null): RecordListItem[] =>
  getDb()
    .select()
    .from(recordCatalog)
    .where(sameTenant(recordCatalog.tenantId, shownTenant(tenantId)))
    .orderBy(asc(sql`rowid`))
    .all()
    .map((row) => ({
      id: row.id,
      name: row.name,
      kind: row.kind,
      nounKey: row.nounKey ?? '',
      entryNounKey: row.entryNounKey ?? '',
      currency: row.currency,
      direction: row.direction,
      // Through the guard, not straight out of the column: a device holds rows a
      // NEWER server wrote, and a name this build has no glyph for would draw an
      // empty square. Unknown reads as none, which is the default glyph.
      icon: isRecordIconId(row.icon) ? row.icon : null,
      entryCount: row.entryCount,
      headlineValue: row.headlineValue,
      lastEntryAt: row.lastEntryAt,
      // Null for an owner AND for a device that has not synced since migration
      // 8. `record-grant.ts` reads both as permissive; the note there says why.
      grant: unpackGrant(row),
      ...(row.quiet === true ? { quiet: true } : {}),
    }));

/**
 * Today, summed from the mirror. Pending rows count — a sale saved offline is
 * still money today. The currency rule (pick, then exclude records carrying
 * another) lives in today-totals.ts and mirrors the server line for line.
 */
export const todayTotals = (today: string, tenantId?: string | null): RecordListToday => {
  const db = getDb();
  const tenant = shownTenant(tenantId);
  const catalog = db
    .select({ currency: recordCatalog.currency, direction: recordCatalog.direction })
    .from(recordCatalog)
    .where(sameTenant(recordCatalog.tenantId, tenant))
    .orderBy(asc(sql`rowid`))
    .all();
  const currency = pickTodayCurrency(catalog);
  if (currency === null) return { currency: null, in: 0, out: 0 };
  // The tenant predicate goes on the MIRROR and not on the joined catalog, even
  // though the join makes the two equivalent: `entry_mirror_tenant_day` leads on
  // the tenant and then the day, so this pair of equalities is one seek.
  const rows = db
    .select({ signedValue: entryMirror.signedValue, sourceChannel: entryMirror.sourceChannel })
    .from(entryMirror)
    .innerJoin(recordCatalog, eq(recordCatalog.id, entryMirror.recordId))
    .where(
      and(
        sameTenant(entryMirror.tenantId, tenant),
        eq(entryMirror.day, today),
        eq(entryMirror.isCurrent, true),
        isNull(entryMirror.deletedAt),
        eq(recordCatalog.currency, currency),
      ),
    )
    .all();
  // A row a link posted is the same money as the row that fired it: counted once.
  return toTodayTotals(currency, countedOnce(rows));
};

/* ----------------------------------------------------------------- writes */

/* ------------------------------------------------------- the search index */

/*
 * Every write below is called from inside the same transaction as the mirror
 * write that caused it. That is not a convenience: an entry whose document did
 * not commit is an entry a shop can never find again, and nothing crashes to
 * say so. There are eight seams in this file and each one is named at its
 * call site.
 *
 * `rebuildSearchDocs` is the one repair primitive. It is the schema-change
 * handler, the first-run backfill, and the fix for any drift — so a bad index
 * is always one call away from being right.
 */

/** The sync_meta key that says a record's documents are built and current. */
const searchVerKey = (recordId: string): string => `search_ver:${recordId}`;

/**
 * The sync_meta key that says how far a rebuild of a record has come.
 *
 * Its value is `<SEARCH_INDEX_VERSION>:<rowid>`: every live row at or below
 * that rowid has its document. It lets a pass that was killed part-way go on
 * from there at the next launch, where it used to start again at the first row,
 * and never finished a record of lakhs of rows.
 *
 * `markSearchStale` removes it, so anything that makes the documents stale also
 * sends the next chunk back to the first row. A chunk checks for it before it
 * writes, and a record is stamped only while it is still there.
 */
const searchAtKey = (recordId: string): string => `search_at:${recordId}`;

/**
 * Bump this to re-index every device on the next launch.
 *
 * The stamp holds it, so changing what a document contains — a new field type
 * indexed, a rendering fixed — becomes a one-line change here rather than a
 * migration.
 */
export const SEARCH_INDEX_VERSION = 1;

/**
 * Write one entry's document, replacing whatever it had.
 *
 * The delete first is what makes this idempotent, which matters because
 * `applyPulled` upserts over rows it may have seen before.
 *
 * The two inserts run back to back ON PURPOSE and this function is the only
 * place they do: the second reads `last_insert_rowid()`, so any statement
 * slipped between them would give the document the wrong id.
 */
const writeDoc = (
  entryId: string,
  recordId: string,
  day: string | null,
  doc: SearchDocument,
): void => {
  forgetDoc(entryId);
  runRaw(insertDocSql(), [doc.title, doc.body, doc.refs]);
  runRaw(linkDocSql(), [entryId, recordId, day]);
};

/**
 * `writeDoc` for a whole chunk: the same four statements, each prepared once.
 *
 * The backfill writes every document of a first download in one pass, which
 * is lakhs of rows. Through `runRaw` each statement is prepared again for
 * every row, and the pass ran at about 690 documents a second on the iOS
 * simulator. `last_insert_rowid()` still pairs the two inserts: all four
 * statements are on the one connection, run back to back.
 */
const withDocWriter = <T>(
  work: (write: (entryId: string, recordId: string, day: string | null, doc: SearchDocument) => void) => T,
): T =>
  withPrepared(deleteDocWordsSql(), (dropWords) =>
    withPrepared(deleteDocRowSql(), (dropRow) =>
      withPrepared(insertDocSql(), (insertWords) =>
        withPrepared(linkDocSql(), (link) =>
          work((entryId, recordId, day, doc) => {
            dropWords([entryId]);
            dropRow([entryId]);
            insertWords([doc.title, doc.body, doc.refs]);
            link([entryId, recordId, day]);
          }),
        ),
      ),
    ),
  );

/**
 * The live lines the mirror holds under some entries of one record, at both
 * levels, keyed by entry id (docs/plans/entry-lines-and-layout.md, Decision
 * 19). Empty for a record with no lines field, and then it reads nothing. A
 * lines record whose schema has not arrived adds nothing yet: its words cannot
 * be rendered, and its parent's documents are rebuilt when it lands.
 */
const mirroredLinesOf = (
  schema: RecordSchemaResponse,
  entryIds: readonly string[],
): Map<string, { schema: RecordSchemaResponse; values: EntryValues }[]> => {
  const found = new Map<string, { schema: RecordSchemaResponse; values: EntryValues }[]>();
  if (entryIds.length === 0) return found;
  for (const lines of linesUnder(schema, schemaFor)) {
    const path = rootPathOf(lines.rootFieldId);
    const rows = queryRaw<{ rootId: string | null; valuesJson: string }>(linesUnderSql(entryIds.length), [
      path,
      lines.recordId,
      path,
      ...entryIds,
    ]);
    for (const row of rows) {
      if (row.rootId === null) continue;
      let values: EntryValues = {};
      try {
        values = JSON.parse(row.valuesJson) as EntryValues;
      } catch {
        continue;
      }
      found.set(row.rootId, [...(found.get(row.rootId) ?? []), { schema: lines.schema, values }]);
    }
  }
  return found;
};

/** One entry's document, with the words of its lines folded in. */
const docOfEntry = (schema: RecordSchemaResponse, entryId: string, values: EntryValues): SearchDocument =>
  docWithLines(schema, values, mirroredLinesOf(schema, [entryId]).get(entryId) ?? []);

/**
 * Write an entry's document again from the mirror, because its lines moved.
 * Answers the entry's record, or null when the mirror holds no live row for
 * it. With `write` false it only answers, for a pass that indexes later.
 */
const refoldEntry = (entryId: string, write: boolean): { recordId: string; written: boolean } | null => {
  const row = getDb()
    .select({ recordId: entryMirror.recordId, day: entryMirror.day, valuesJson: entryMirror.valuesJson })
    .from(entryMirror)
    .where(and(eq(entryMirror.entryId, entryId), eq(entryMirror.isCurrent, true), isNull(entryMirror.deletedAt)))
    .get();
  if (row === undefined) return null;
  const schema = write ? schemaFor(row.recordId) : null;
  if (schema === null) return { recordId: row.recordId, written: false };
  let values: EntryValues = {};
  try {
    values = JSON.parse(row.valuesJson) as EntryValues;
  } catch {
    values = {};
  }
  writeDoc(entryId, row.recordId, row.day, docOfEntry(schema, entryId, values));
  return { recordId: row.recordId, written: true };
};

/**
 * Render and write, when a schema is in hand.
 *
 * A line is never a document (Decision 19): its words live in the document of
 * the entry at the top of its tree, so writing a line writes that entry's
 * document again instead.
 */
const indexEntry = (
  schema: RecordSchemaResponse,
  entryId: string,
  recordId: string,
  day: string | null,
  values: EntryValues,
): void => {
  if (isLinesSchema(schema)) {
    forgetDoc(entryId);
    const root = rootOfLine(schema, values);
    if (root !== null) refoldEntry(root, true);
    return;
  }
  writeDoc(entryId, recordId, day, docOfEntry(schema, entryId, values));
};

/** Forget one entry's document. Safe when it has none. */
const forgetDoc = (entryId: string): void => {
  runRaw(deleteDocWordsSql(), [entryId]);
  runRaw(deleteDocRowSql(), [entryId]);
};

/** Forget the documents of a batch of entries, in two statements rather than two per row. */
const forgetDocs = (entryIds: readonly string[]): void => {
  if (entryIds.length === 0) return;
  const ids = [...entryIds];
  runRaw(deleteDocsWordsSql(ids.length), ids);
  runRaw(deleteDocsRowsSql(ids.length), ids);
};

/** Forget every document of these records. */
const forgetRecordDocs = (recordIds: readonly string[]): void => {
  if (recordIds.length === 0) return;
  const ids = [...recordIds];
  runRaw(deleteRecordWordsSql(ids.length), ids);
  runRaw(deleteRecordRowsSql(ids.length), ids);
};

/**
 * A record whose entries arrived before its schema did.
 *
 * A document cannot be rendered without a schema, so such an entry is left
 * unindexed — and the stamp is cleared so the backfill comes back for it. Doing
 * nothing here is the drift this whole section exists to prevent.
 */
const markSearchStale = (recordId: string): void => {
  getDb()
    .delete(syncMeta)
    .where(inArray(syncMeta.key, [searchVerKey(recordId), searchAtKey(recordId)]))
    .run();
};

/** The grouped-field indexes this device holds for one record, by name. */
const fieldIndexesOf = (recordId: string): string[] => {
  const prefix = fieldIndexPrefixFor(recordId);
  return queryRaw<{ name: string }>(
    `SELECT name FROM sqlite_master WHERE type = 'index' AND substr(name, 1, length(?)) = ?`,
    [prefix, prefix],
  ).map((row) => row.name);
};

const dropFieldIndexes = (recordId: string): void => {
  for (const name of fieldIndexesOf(recordId)) execRaw(`DROP INDEX IF EXISTS "${name.replaceAll('"', '""')}"`);
};

/**
 * One missing grouped-field index built, or false when none is missing.
 *
 * A schema that changes builds its own indexes as it lands. This is for the
 * rest: a device that already held its records when the indexes were
 * introduced, whose schemas will not change on their own. One index at a time,
 * because building one reads the whole record and the driver yields to the
 * screen between them.
 */
export const buildOneFieldIndex = (): boolean => {
  const held = getDb().select({ schemaJson: schemaCache.schemaJson }).from(schemaCache).all();
  for (const row of held) {
    const schema = JSON.parse(row.schemaJson) as RecordSchemaResponse;
    const next = fieldIndexChanges(schema, fieldIndexesOf(schema.recordId))[0];
    if (next !== undefined) {
      execRaw(next);
      return true;
    }
  }
  return false;
};

/** Whether the device holds any row of this record, current, deleted or pending. */
const hasMirroredRows = (recordId: string): boolean =>
  getDb()
    .select({ entryId: entryMirror.entryId })
    .from(entryMirror)
    .where(eq(entryMirror.recordId, recordId))
    .limit(1)
    .get() !== undefined;

/** Say a record's documents are built and current. */
const stampSearch = (recordId: string): void => {
  getDb()
    .insert(syncMeta)
    .values({ key: searchVerKey(recordId), value: String(SEARCH_INDEX_VERSION) })
    .onConflictDoUpdate({
      target: syncMeta.key,
      set: { value: String(SEARCH_INDEX_VERSION) },
    })
    .run();
};

/** Is this record's index built, and built by this version of the renderer? */
export const searchIsCurrent = (recordId: string): boolean => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, searchVerKey(recordId)))
    .get();
  return row !== undefined && Number(row.value) === SEARCH_INDEX_VERSION;
};

/**
 * Build one record's documents, one chunk at a time.
 *
 * THE one repair primitive, and it is several functions rather than one
 * because a record of fifty thousand rows takes seconds. Doing that in a single call
 * would freeze the screen, so the caller drives it and hands the thread back
 * between chunks. `search-backfill.ts` is that caller.
 *
 * **It does not clear the record first.** Clearing would make it
 * unsearchable for the whole rebuild, and a shop searching mid-rebuild would
 * be told its sales do not exist. So every document is written OVER in place,
 * and `finishSearchRebuild` removes the ones left pointing at nothing.
 *
 * A kill part-way leaves the finished chunks committed, the stamp unset and
 * the cursor (`searchAtKey`) at the last chunk, which is exactly the state that
 * makes the next launch go on from there.
 *
 * The caller holds `after`, the place the cursor names, and hands it to every
 * call. A call that finds the cursor gone, or somewhere else, answers `'stale'`
 * and writes nothing. Gone: something marked the record stale in between, so
 * the rows before `after` may need their documents again. Somewhere else:
 * another writer moved it. Either way the caller asks `searchRebuildStart`
 * again, which answers the first row or the other writer's place.
 */

/** How many live rows this record must end up with a document for. */
export const searchRebuildSize = (recordId: string): number =>
  queryRaw<{ n: number }>(liveCountSql(), [recordId])[0]?.n ?? 0;

/**
 * A place in a record's rebuild: a writer and a rowid, in the order of
 * `entry_mirror_record_live`. Every live row before it has its document. The
 * first place is `REBUILD_FROM_START`: NULL sorts first, and no rowid is 0.
 */
export type RebuildCursor = { readonly by: string | null; readonly id: number };

export const REBUILD_FROM_START: RebuildCursor = { by: null, id: 0 };

const sameCursor = (left: RebuildCursor | null, right: RebuildCursor): boolean =>
  left !== null && left.by === right.by && left.id === right.id;

/** The place the cursor names, or null when there is none for this renderer. */
const readSearchCursor = (recordId: string): RebuildCursor | null => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, searchAtKey(recordId)))
    .get();
  if (row === undefined) return null;
  const colon = row.value.indexOf(':');
  if (Number(row.value.slice(0, colon)) !== SEARCH_INDEX_VERSION) return null;
  try {
    const at = JSON.parse(row.value.slice(colon + 1)) as { by?: unknown; id?: unknown };
    const by = at.by === null || typeof at.by === 'string' ? at.by : undefined;
    return by !== undefined && Number.isInteger(at.id) ? { by, id: at.id as number } : null;
  } catch {
    return null;
  }
};

const writeSearchCursor = (recordId: string, at: RebuildCursor): void => {
  const value = `${SEARCH_INDEX_VERSION}:${JSON.stringify({ by: at.by, id: at.id })}`;
  getDb()
    .insert(syncMeta)
    .values({ key: searchAtKey(recordId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/**
 * Where a rebuild of this record starts: the saved cursor, or the first row.
 *
 * A record with no cursor gets one at the start here, so that a stale mark
 * after this call is seen: it removes the cursor, and the next chunk finds it
 * gone.
 *
 * NULL when no schema for this record has arrived yet, for the reason
 * `searchRebuildChunk` gives: such a record must not be stamped, even empty.
 */
export const searchRebuildStart = (recordId: string): RebuildCursor | null => {
  if (schemaFor(recordId) === null) {
    markSearchStale(recordId);
    return null;
  }
  const at = readSearchCursor(recordId);
  if (at !== null) return at;
  writeSearchCursor(recordId, REBUILD_FROM_START);
  return REBUILD_FROM_START;
};

/**
 * The next places to index, after `after`, from the index alone.
 *
 * All from one writer: the rest of `after`'s, or else the first rows of the
 * next one. Empty only when the record has no row after `after`.
 */
export const searchRebuildIds = (recordId: string, after: RebuildCursor, limit: number): RebuildCursor[] => {
  const read = (statement: string, params: (string | number | null)[]): RebuildCursor[] =>
    queryRaw<{ rowId: number; createdBy: string | null }>(statement, params).map((row) => ({
      by: row.createdBy,
      id: row.rowId,
    }));
  const same = read(rebuildIdsSameWriterSql(), [recordId, after.by, after.id, limit]);
  if (same.length > 0) return same;
  return after.by === null
    ? read(rebuildIdsFirstWriterSql(), [recordId, limit])
    : read(rebuildIdsNextWriterSql(), [recordId, after.by, limit]);
};

/**
 * Index one chunk of a record: the rows with these ids, which must follow `after`.
 *
 * Returns how many documents it wrote, NULL when no schema has arrived for
 * this record yet, or `'stale'` when the cursor is no longer at `after`. The
 * first two must be told apart: nothing written can also mean an EMPTY record,
 * and an empty one has to be stamped as done or every pass would revisit it
 * forever, while a record with no schema must NOT be stamped or it would never
 * be indexed at all.
 *
 * An id whose row went since the ids were read writes nothing, and its old
 * document, if any, is an orphan that `finishSearchRebuild` removes.
 */
export const searchRebuildChunk = (
  recordId: string,
  after: RebuildCursor,
  places: readonly RebuildCursor[],
): number | null | 'stale' => {
  const schema = schemaFor(recordId);
  if (schema === null) {
    markSearchStale(recordId);
    return null;
  }
  const last = places[places.length - 1];
  if (last === undefined) return 0;
  const ids = places.map((place) => place.id);
  const rows = queryRaw<{ rowId: number; entryId: string; day: string | null; valuesJson: string }>(
    rebuildRowsSql(ids.length),
    [...ids, recordId],
  );
  // The lines of the whole chunk, one read per lines record, folded into
  // each entry's document (Decision 19). Nothing is read for a record with
  // no lines field.
  const lines = mirroredLinesOf(
    schema,
    rows.map((row) => row.entryId),
  );

  return getDb().transaction(() => {
    if (!sameCursor(readSearchCursor(recordId), after)) return 'stale' as const;
    withDocWriter((write) => {
      for (const row of rows) {
        // Unreadable JSON is a row nothing else can draw either. It gets an
        // empty document rather than stopping the rebuild.
        let values: EntryValues = {};
        try {
          values = JSON.parse(row.valuesJson) as EntryValues;
        } catch {
          values = {};
        }
        write(row.entryId, recordId, row.day, docWithLines(schema, values, lines.get(row.entryId) ?? []));
      }
    });
    writeSearchCursor(recordId, last);
    return rows.length;
  });
};

/**
 * Drop the documents of entries that are gone, then stamp the record done.
 *
 * Only while the cursor is still at `after`. False, and nothing stamped, when
 * the record went stale during the pass: the next pass comes back for it.
 */
export const finishSearchRebuild = (recordId: string, after: RebuildCursor): boolean =>
  getDb().transaction(() => {
    if (!sameCursor(readSearchCursor(recordId), after)) return false;
    runRaw(deleteOrphanWordsSql(), [recordId, recordId]);
    runRaw(deleteOrphanRowsSql(), [recordId, recordId]);
    stampSearch(recordId);
    getDb().delete(syncMeta).where(eq(syncMeta.key, searchAtKey(recordId))).run();
    return true;
  });

/**
 * The whole record, synchronously. For a test, and for a record small enough
 * that nobody would feel it.
 *
 * Production goes through `search-backfill.ts`, which drives the functions
 * above and yields to the screen between chunks. Nothing else writes while this
 * runs, so it never sees `'stale'` from itself; if it does, it starts again.
 */
export const rebuildSearchDocs = (recordId: string): number => {
  let written = 0;
  let after = searchRebuildStart(recordId);
  if (after === null) return written;
  for (;;) {
    const places = searchRebuildIds(recordId, after, REBUILD_CHUNK);
    if (places.length === 0) break;
    const wrote: number | null | 'stale' = searchRebuildChunk(recordId, after, places);
    if (wrote === null) return written;
    if (wrote === 'stale') {
      const restart = searchRebuildStart(recordId);
      if (restart === null) return written;
      after = restart;
      continue;
    }
    written += wrote;
    after = places[places.length - 1]!;
  }
  finishSearchRebuild(recordId, after);
  return written;
};

/**
 * The records whose index is missing, or was built by an older renderer.
 *
 * What the backfill works through on every launch. Empty means every record on
 * this device is searchable and current.
 */
export const recordsNeedingSearch = (): string[] =>
  queryRaw<{ recordId: string }>(needsIndexSql(), [String(SEARCH_INDEX_VERSION)]).map(
    (row) => row.recordId,
  );

/* ------------------------------------------------------------ reading search */

/**
 * One row of a search result.
 *
 * It carries a WHOLE `EntryListRow` and not a summary, because a result is
 * drawn by the entry list's own row component. Anything less would make the
 * same entry behave differently in the two places: a pending row tappable
 * here and not there, a link-written row missing its badge.
 */
export type SearchHit = {
  readonly recordId: string;
  readonly day: string | null;
  readonly signedValue: number | null;
  readonly entry: EntryListRow;
};

/**
 * A record whose own name matched. Drawn above the entries, as a door.
 *
 * The count comes with it because the row has to say what is behind the door.
 * Typing "sales" is almost always "take me to Sales", and the row answering
 * "Sales - 8 sales" makes that one tap obvious.
 */
export type SearchRecord = {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly entryCount: number;
  readonly entryNounKey: string | null;
};

export type SearchAnswer = {
  readonly records: readonly SearchRecord[];
  readonly hits: readonly SearchHit[];
  /**
   * The entries whose OWN names matched.
   *
   * The screen needs them to tell a reference match from an ordinary one: a
   * sale is in this list's results because it points at one of these, and that
   * is what "Customer - Ramesh Kumar" on the row is saying.
   */
  readonly seeds: ReadonlySet<string>;
  /**
   * True when nothing matched and these are the CLOSEST rows instead.
   *
   * The screen says so, and lights nothing: no term matched, so a highlight
   * would claim something untrue.
   */
  readonly near: boolean;
  /**
   * How many entries of each record TRULY match, keyed by record id.
   *
   * `hits` is capped at RESULT_LIMIT across every record, so the number of hits
   * a record got is a fact about the cap, not about the shop: a worker with
   * eighty-two wage rows read "Labour wages 49". This is the uncapped count,
   * under the same matching rule, so a heading can say "49 of 82".
   *
   * Null on the near path, and deliberately. There a row is a match only
   * because JavaScript measured its edit distance, over at most
   * FALLBACK_SCAN_LIMIT candidates that SQL fetched unranked — a set no count
   * statement can reproduce without doing the whole scan. Rather than print a
   * number it cannot stand behind, the heading prints none.
   */
  readonly totals: ReadonlyMap<string, number> | null;
};

export const EMPTY_ANSWER: SearchAnswer = {
  records: [],
  hits: [],
  seeds: new Set(),
  near: false,
  totals: new Map(),
};

type HitRow = EntryMirrorRow & {
  entryId: string;
  recordId: string;
  day: string | null;
  signedValue: number | null;
  /** Only the loose statement returns these, for the edit-distance pass. */
  title?: string;
  body?: string;
};

/**
 * `toListRow` does the mapping, so a search row and a list row are parsed by
 * one function. Two parsers would drift, and the drift would show as the same
 * entry reading differently in the two places.
 *
 * Guarded, unlike the list's own call. A search spans EVERY record, so one row
 * of unreadable JSON would take the whole search screen down rather than one
 * record's list. It draws as an entry with no values instead.
 *
 * Note on `pending`: these rows come through `queryRaw` and not Drizzle, so the
 * column arrives as 0 or 1 rather than as a boolean. `toListRow` tests it for
 * truth, which is correct for both, and this is the only place that distinction
 * exists.
 */
const toHit = (row: HitRow): SearchHit => {
  let entry: EntryListRow;
  try {
    entry = toListRow(row);
  } catch {
    entry = {
      entryId: row.entryId,
      version: row.version,
      values: {},
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      createdBy: row.createdBy ?? 'local',
      sourceChannel: row.sourceChannel ?? 'typed',
      linkId: null,
      sourceEntryId: null,
    };
  }
  return { recordId: row.recordId, day: row.day, signedValue: row.signedValue, entry };
};

/**
 * What the strict search matches on, worked out once.
 *
 * Shared by `searchEverything` and `searchRecordHits`, because "which rows
 * match" must have ONE answer: a section that showed forty-nine of eighty-two
 * and then, asked for the rest, matched by a different rule would show a
 * different eighty-two.
 */
type StrictSteps = {
  readonly records: readonly SearchRecord[];
  readonly direct: string | null;
  readonly seeds: ReadonlySet<string>;
  /** The reference step's MATCH, or null when no row's own name matched. */
  readonly through: string | null;
};

const strictSteps = (typed: string, scoped: boolean, scope: readonly string[]): StrictSteps => {
  const records = queryRaw<SearchRecord>(recordMatchSql(scoped), [
    containsPattern(typed),
    ...scope,
    3,
  ]);

  // The reference step. Seeded from the TITLE column only, because a note
  // mentioning Ramesh is not Ramesh.
  const seeds = new Set<string>();
  const titleMatch = titleMatchExpression(typed);
  if (titleMatch !== null) {
    const seedRows = queryRaw<{ entryId: string }>(seedSql(scoped), [
      titleMatch,
      ...scope,
      REF_SEED_LIMIT,
    ]);
    for (const row of seedRows) {
      seeds.add(row.entryId);
    }
  }
  const through = seeds.size > 0 ? refsMatchExpression([...seeds].map(refToken)) : null;

  return { records, direct: matchExpression(typed), seeds, through };
};

/**
 * How many entries of each record truly match, uncapped.
 *
 * The same union the hits are drawn from — the direct match OR the reference
 * step, counted as ONE MATCH so a row reached both ways counts once — and, for
 * a record whose name matched, every live entry it holds, because that is what
 * such a record brings.
 *
 * **Skipped when nothing was capped.** Every step asks for RESULT_LIMIT rows,
 * so an answer holding fewer than that ran every step to its end, and the hits
 * ARE the matches: counting them again would be two more statements per
 * keystroke to learn nothing. Only an answer that reached the cap pays for the
 * count, and that count is a grouped MATCH that reads no values.
 */
const matchTotals = (
  steps: StrictSteps,
  hits: readonly SearchHit[],
  scoped: boolean,
  scope: readonly string[],
): Map<string, number> => {
  const totals = new Map<string, number>();
  if (hits.length < RESULT_LIMIT) {
    for (const hit of hits) totals.set(hit.recordId, (totals.get(hit.recordId) ?? 0) + 1);
    return totals;
  }

  const either = eitherExpression(steps.direct, steps.through);
  if (either !== null) {
    for (const row of queryRaw<{ recordId: string; n: number }>(matchCountSql(scoped), [
      either,
      ...scope,
    ])) {
      totals.set(row.recordId, row.n);
    }
  }
  if (steps.records.length > 0) {
    // Over the MATCH count, not added to it: a record whose name matched shows
    // every live entry, and the entries that also matched on their own words
    // are among them.
    for (const row of queryRaw<{ recordId: string; n: number }>(
      recordLiveCountSql(steps.records.length, scoped),
      [...steps.records.map((one) => one.id), ...scope],
    )) {
      totals.set(row.recordId, row.n);
    }
  }
  return totals;
};

/**
 * Search every record on this device.
 *
 * Three queries in the ordinary case, and they are three because of one fact:
 * a sale holds the customer's ID, not his name. So finding "everything for
 * Ramesh" cannot be one MATCH.
 *
 *   1. the records whose own name matched    12 rows, a LIKE, instant
 *   2. the entries whose own words matched   one MATCH, ranked
 *   3. the entries pointing at the rows      one MATCH over `refs`
 *      whose own NAME matched
 *
 * Direct hits come first and the expansion is merged after them, deduplicated
 * on entry id: a row that matched on its own words should not be pushed down by
 * one that matched through a link.
 *
 * A FOURTH query runs only when the first three found nothing at all: the loose
 * fallback, sorted by edit distance in JavaScript. That is this app's whole
 * answer to typo tolerance, because FTS5 has no fuzzy operator and the
 * extensions that would give it one are not compiled into the build.
 */
export const searchEverything = (typed: string, tenantId?: string | null): SearchAnswer => {
  if (!isSearchable(typed)) return EMPTY_ANSWER;

  /*
   * "Every record on this device" means every record of ONE business.
   *
   * The index itself is not partitioned and does not need to be — a document is
   * addressed by record id, and record ids are uuids — so the tenant is a
   * predicate on the statements rather than a second index. `scoped` is false
   * only on a device that cannot name a business at all, where there is nothing
   * to keep apart.
   */
  const tenant = shownTenant(tenantId);
  const scoped = tenant !== null;
  /** The tenant parameter, in the one position each statement puts it. */
  const scope = tenant === null ? [] : [tenant];

  const steps = strictSteps(typed, scoped, scope);
  const { records, direct, seeds, through } = steps;
  const hits: SearchHit[] = [];
  const held = new Set<string>();

  if (direct !== null) {
    for (const row of queryRaw<HitRow>(searchSql(scoped), [direct, ...scope, RESULT_LIMIT])) {
      held.add(row.entryId);
      hits.push(toHit(row));
    }
  }

  if (through !== null) {
    for (const row of queryRaw<HitRow>(searchSql(scoped), [through, ...scope, RESULT_LIMIT])) {
      if (held.has(row.entryId)) continue;
      held.add(row.entryId);
      hits.push(toHit(row));
    }
  }

  /**
   * A record whose NAME matched brings its entries with it.
   *
   * Without this, typing "sales" answered with a door and nothing else — and
   * tapping the door left a person at the top of it, looking for the
   * thing all over again. The search had done no work for them.
   *
   * AFTER the direct hits, never before: a row that matched on its own words is
   * a better answer than a row that merely lives in a record with a matching
   * name. And under the same cap, so a record of five thousand entries can
   * never push another record's real match off the list.
   */
  // ONE RECORD AT A TIME, in the order the records ranked. Asking for all of
  // them in one statement sorted every row by day across the lot, which threw
  // the record ranking away: a search for "sales" put a "Scrap sales" row above
  // every Sales row, because that one happened to be a day newer. At most three
  // records match, so this is at most three small statements.
  //
  // Each asks for the WHOLE cap, not what is left of it, and stops when the cap
  // is full. Asking for what was left came back short whenever the newest rows
  // were already held as direct hits — the duplicates were skipped and the
  // space they had been counted against stayed empty. Worse, it made a short
  // answer ambiguous: fewer than RESULT_LIMIT hits no longer meant "every step
  // ran to its end", which is exactly what `matchTotals` relies on.
  for (const record of records) {
    if (hits.length >= RESULT_LIMIT) break;
    const rows = queryRaw<HitRow>(recordEntriesSql(1, scoped), [record.id, ...scope, RESULT_LIMIT]);
    for (const row of rows) {
      if (hits.length >= RESULT_LIMIT) break;
      if (held.has(row.entryId)) continue;
      held.add(row.entryId);
      hits.push(toHit(row));
    }
  }

  if (hits.length > 0 || records.length > 0) {
    const shown = hits.slice(0, RESULT_LIMIT);
    return { records, hits: shown, seeds, near: false, totals: matchTotals(steps, hits, scoped, scope) };
  }

  // Nothing matched. Try the loose query, and let edit distance order it.
  const loose = looseExpression(typed);
  if (loose === null) return EMPTY_ANSWER;
  const candidates = queryRaw<HitRow>(looseSql(scoped), [loose, ...scope, FALLBACK_SCAN_LIMIT]);
  const ranked = nearest(
    typed,
    candidates.map((row) => ({ row, text: `${row.title ?? ''} ${row.body ?? ''}` })),
  );
  if (ranked.length === 0) return EMPTY_ANSWER;

  const nearHits: SearchHit[] = [];
  const nearHeld = new Set<string>();
  for (const one of ranked.slice(0, RESULT_LIMIT)) {
    nearHeld.add(one.row.entryId);
    nearHits.push(toHit(one.row));
  }

  /**
   * The reference step, on the fallback path too.
   *
   * A shop mistyping an item's name wants the SALES of that item, not the item.
   * Without this the near miss answers with a directory row and stops one tap
   * short of the thing that was being looked for.
   *
   * Seeded from the rows whose own NAME was the near match — a row with an
   * empty title matched on its body, and a body is not a name to expand
   * through.
   */
  const nearSeeds = new Set<string>();
  for (const one of ranked.slice(0, REF_SEED_LIMIT)) {
    if ((one.row.title ?? '') !== '') nearSeeds.add(one.row.entryId);
  }
  if (nearSeeds.size > 0) {
    const through = refsMatchExpression([...nearSeeds].map(refToken));
    if (through !== null) {
      for (const row of queryRaw<HitRow>(searchSql(scoped), [through, ...scope, RESULT_LIMIT])) {
        if (nearHeld.has(row.entryId)) continue;
        nearHeld.add(row.entryId);
        nearHits.push(toHit(row));
      }
    }
  }

  return {
    records: [],
    hits: nearHits.slice(0, RESULT_LIMIT),
    // Carried so a row reached through a link still says so. Nothing is LIT on
    // this path, because no term matched — but "Item - IPhone" is still the
    // honest reason the row is here.
    seeds: nearSeeds,
    near: true,
    // No count on this path. See `SearchAnswer.totals`: a near match is decided
    // in JavaScript over a bounded, unranked scan, so SQL cannot count them.
    totals: null,
  };
};

/**
 * More of ONE record's matches than the capped answer gave it.
 *
 * The door behind a section heading that reads "49 of 82". The strict rule
 * only, because that is the only path that knows its total: the near path draws
 * no count and so offers no "more".
 *
 * The same steps as `searchEverything`, in the same order — direct hits, then
 * the rows reached through a reference, then, for a record whose NAME matched,
 * its entries newest first — so the rows the section already showed come back
 * at the top and in the order they were in. Asked for `limit` rows and not a
 * page after an offset: two ranked statements and a merge cannot be paged by
 * OFFSET without drifting, and a re-read of a few hundred rows is cheap.
 */
export const searchRecordHits = (
  typed: string,
  recordId: string,
  limit: number,
  tenantId?: string | null,
): SearchHit[] => {
  if (!isSearchable(typed) || limit <= 0) return [];
  const tenant = shownTenant(tenantId);
  const scoped = tenant !== null;
  const scope = tenant === null ? [] : [tenant];
  const { records, direct, through } = strictSteps(typed, scoped, scope);

  const hits: SearchHit[] = [];
  const held = new Set<string>();
  const take = (rows: readonly HitRow[]): void => {
    for (const row of rows) {
      if (hits.length >= limit) return;
      if (held.has(row.entryId)) continue;
      held.add(row.entryId);
      hits.push(toHit(row));
    }
  };

  for (const expression of [direct, through]) {
    if (expression === null) continue;
    take(queryRaw<HitRow>(searchSql(scoped, true), [expression, ...scope, recordId, limit]));
  }
  if (hits.length < limit && records.some((one) => one.id === recordId)) {
    take(queryRaw<HitRow>(recordEntriesSql(1, scoped), [recordId, ...scope, limit]));
  }
  return hits;
};

/**
 * The last few things somebody searched for.
 *
 * One row in sync_meta, newest first, capped. Written when a result is TAPPED
 * and not on every keystroke: what a person typed on the way to an answer is
 * not a search they made.
 *
 * ONE LIST PER BUSINESS, because what was typed is a business's own business.
 * A key per tenant rather than one shared list: "Ramesh" typed in one shop is
 * a customer's name, and it has no place in the other shop's suggestions. A
 * device that cannot name a business keeps the old shared key, so nothing that
 * exists today changes; an upgrade starts each business's list empty, which
 * costs a person the eight words they last searched for and nothing else.
 */
const RECENTS_KEY = 'recent_searches';
const RECENTS_CAP = 8;

const recentsKey = (tenantId?: string | null): string => {
  const tenant = shownTenant(tenantId);
  return tenant === null ? RECENTS_KEY : `${RECENTS_KEY}:${tenant}`;
};

export const recentSearches = (tenantId?: string | null): string[] => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, recentsKey(tenantId)))
    .get();
  if (row === undefined) return [];
  try {
    const held = JSON.parse(row.value) as unknown;
    if (!Array.isArray(held)) return [];
    return held.filter((one): one is string => typeof one === 'string').slice(0, RECENTS_CAP);
  } catch {
    return [];
  }
};

export const rememberSearch = (typed: string, tenantId?: string | null): void => {
  const word = typed.trim();
  if (word === '') return;
  const key = recentsKey(tenantId);
  const next = [word, ...recentSearches(tenantId).filter((one) => one !== word)].slice(
    0,
    RECENTS_CAP,
  );
  getDb()
    .insert(syncMeta)
    .values({ key, value: JSON.stringify(next) })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value: JSON.stringify(next) } })
    .run();
};

export const forgetSearches = (tenantId?: string | null): void => {
  getDb().delete(syncMeta).where(eq(syncMeta.key, recentsKey(tenantId))).run();
};

/**
 * The mirror's rule for an entry a person saved directly, the phone's half of
 * the server's `savedEntries` (docs/plans/entry-lines-and-layout.md, Decision
 * 19). A lines record never reaches the catalog, while its entries are pulled
 * like any other, so a row counts only when its record is in the catalog. The
 * catalog-scoped reads (todayTotals, writerCount, catalogCounts) already obey it.
 */
const SAVED_ENTRIES = 'record_id IN (SELECT id FROM record_catalog)';

/**
 * How many live entries this business holds, for the "nothing found" line.
 *
 * Per business, because the line it draws is "nothing matched, out of 412" —
 * and 412 counted across two shops would be a number about neither of them.
 */
export const totalEntryCount = (tenantId?: string | null): number => {
  const tenant = shownTenant(tenantId);
  return (
    queryRaw<{ n: number }>(
      `SELECT count(*) AS n FROM entry_mirror
        WHERE is_current = 1 AND deleted_at IS NULL AND ${SAVED_ENTRIES}${tenant === null ? '' : ' AND tenant_id = ?'}`,
      tenant === null ? [] : [tenant],
    )[0]?.n ?? 0
  );
};

/**
 * The invariant, for tests and for a drift check: documents against live rows.
 *
 * Equal for every record, always. Unequal means a write path forgot its
 * document, and the fix is `rebuildSearchDocs`.
 */
export const searchDocDrift = (recordId: string): { docs: number; live: number } => {
  const docs = queryRaw<{ n: number }>(docCountSql(), [recordId])[0]?.n ?? 0;
  const live = queryRaw<{ n: number }>(liveCountSql(), [recordId])[0]?.n ?? 0;
  return { docs, live };
};

/**
 * One pending row's three writes, inside a transaction somebody else opened.
 *
 * Factored out of `insertPending` when the sheet arrived, because the sheet
 * saves eight of these at once and a per-row transaction would be eight
 * BEGIN/COMMIT pairs for one gesture — and, worse, a half-written day if the
 * fourth one threw. Both callers run exactly these three inserts in exactly
 * this order; the only thing they decide differently is how many times.
 *
 * Two different value maps leave here, and the difference is the whole point.
 * The MIRROR keeps the answers this device worked out, so a sale saved offline
 * shows its Amount at once and counts in the day total. The OUTBOX carries only
 * what a person typed, because the server works the answers out again from the
 * recipe and refuses one that arrives written.
 */
const writePending = (
  tx: DeviceTx,
  recordId: string,
  tenant: string | null,
  typed: EntryValues,
  schema: RecordSchemaResponse,
  now: string,
  lines?: EntryLinesWrite,
  /** The id a card proposed the row under, so an entry of the same turn may name it (L64.1); absent, a new one. */
  givenId?: string,
): PendingRow => {
  const entryId = givenId ?? newLocalId();
  const opId = newLocalId();
  // Worked out first: the measure binding may name a formula field, and then
  // the day total reads the answer rather than an empty slot. With lines, the
  // whole tree is worked out deepest first, and the entry totals its lines.
  const tree = lines === undefined ? null : fillFormulaTree(schema, { entryId, values: typed }, lines);
  const values = tree === null ? fillFormulas(schema, typed) : tree.values;
  // Must be, last, before the entry enters the outbox (F13, F14): any block
  // stops the save here, so a refusal the outbox would make final never leaves.
  holdRules(schema, { entryId, values }, tree === null ? null : tree.lines);
  const pushed = withoutComputed(schema, typed);
  const { day, signedValue } = materialize(schema, values, now);
  keepShownPicks(tx, schema, typed);

  tx.insert(entryMirror)
    .values({
      entryId,
      recordId,
      tenantId: tenant,
      version: 0,
      versionId: null,
      serverSeq: null,
      isCurrent: true,
      deletedAt: null,
      day,
      signedValue,
      pending: true,
      valuesJson: JSON.stringify(values),
      createdBy: 'local',
      sourceChannel: 'typed',
      createdAt: now,
      updatedAt: now,
    })
    .run();
  tx.insert(outbox)
    .values({
      opId,
      kind: 'create',
      recordId,
      tenantId: tenant,
      entryId,
      valuesJson: JSON.stringify(pushed),
      linesJson: lines === undefined ? null : JSON.stringify(withoutComputedLines(schema, lines)),
      fieldSetVer: schema.fieldSetVer,
      failedReason: null,
      createdAt: now,
    })
    .run();
  // The lines go into the mirror in the same transaction, so a sale saved
  // offline lists its items at once, and BEFORE the document: the entry's
  // document folds in the words of the lines it finds under it.
  if (tree !== null) writeLineRows(tx, schema, tree.lines, { parentId: entryId, rootId: entryId }, tenant, now, new Map());
  // SEAM 1 of 8. The schema is right here as an argument, so the document
  // costs one render and joins the same transaction as the row.
  indexEntry(schema, entryId, recordId, day, values);

  return {
    entryId,
    version: 0,
    values,
    createdAt: now,
    updatedAt: now,
    createdBy: 'local',
    sourceChannel: 'typed',
    // A row this device just typed is nobody's derived row.
    linkId: null,
    sourceEntryId: null,
    pending: true,
  };
};

/* ------------------------------------------------- lines on the device */

/*
 * docs/plans/entry-lines-and-layout.md, Decisions 1, 5 and 19. A line is an
 * entry of a lines record, so the mirror holds it as a row like any other, with
 * its parent and root ids in its values exactly as a pull delivers it. It is
 * never a document (its words fold into its root entry's) and never counted
 * (its record is not in the catalog). What is different is only how it is
 * written: always with the entry above it, in that entry's transaction and
 * that entry's outbox op.
 */

/** One `lines` field of a row, with the lines record and schema its lines use. */
type LineRun = {
  readonly linesFieldId: string;
  readonly recordId: string;
  readonly schema: RecordSchemaResponse;
  readonly parentField: string | null;
  readonly rootField: string | null;
};

/**
 * The runs of lines a row holds, from the schema the form saved with: its
 * nested `lineSchemas` first, else the lines record's own cached schema. A
 * run with no schema on this device is left out: nothing can be mirrored
 * for it, and the pull brings it once the schema lands.
 */
const lineRunsOf = (schema: RecordSchemaResponse): LineRun[] =>
  schema.fields.flatMap((field) => {
    if (field.type !== 'lines') return [];
    const spec = linesOf(field.spec);
    if (spec === null) return [];
    const lineSchema = schema.lineSchemas?.[field.id] ?? schemaFor(spec.recordId);
    if (lineSchema === null) return [];
    const refs = lineRefFields(lineSchema);
    return [{ linesFieldId: field.id, recordId: spec.recordId, schema: lineSchema, parentField: refs.parent, rootField: refs.root }];
  });

/** The live mirror rows of one run under one parent, in the order they were written. */
const rowsOfRun = (
  tx: DeviceTx,
  run: LineRun,
  parentId: string,
): { entryId: string; valuesJson: string }[] =>
  run.parentField === null
    ? []
    : tx
        .select({ entryId: entryMirror.entryId, valuesJson: entryMirror.valuesJson })
        .from(entryMirror)
        .where(
          and(
            eq(entryMirror.recordId, run.recordId),
            eq(entryMirror.isCurrent, true),
            isNull(entryMirror.deletedAt),
            sql`json_extract(${entryMirror.valuesJson}, ${rootPathOf(run.parentField)}) = ${parentId}`,
          ),
        )
        .orderBy(asc(entryMirror.createdAt), asc(sql`rowid`))
        .all();

/**
 * The lines the mirror holds under one row, at every level, as a write would
 * carry them (no parent or root id), and each line's stored values as JSON,
 * so a write can tell which lines it actually moved.
 */
const heldLines = (
  tx: DeviceTx,
  schema: RecordSchemaResponse,
  parentId: string,
  depth = 0,
  json: Map<string, string> = new Map(),
): { lines: EntryLinesWrite; json: Map<string, string> } => {
  const lines: Record<string, LineWrite[]> = {};
  // Two levels, never more, whatever a cached schema claims.
  if (depth >= LINES_MAX_DEPTH) return { lines, json };
  for (const run of lineRunsOf(schema)) {
    lines[run.linesFieldId] = rowsOfRun(tx, run, parentId).map((row) => {
      json.set(row.entryId, row.valuesJson);
      let values: EntryValues = {};
      try {
        values = JSON.parse(row.valuesJson) as EntryValues;
      } catch {
        // An unreadable line reads as empty, and its next write replaces it.
      }
      const under = heldLines(tx, run.schema, row.entryId, depth + 1, json).lines;
      return {
        entryId: row.entryId,
        values: withoutLineRefs(run.schema, values),
        ...(Object.keys(under).length === 0 ? {} : { lines: under }),
      };
    });
  }
  return { lines, json };
};

/**
 * The lines the mirror holds under one entry, at both levels, as the form
 * opens them on an edit: each line's values as stored (its worked-out
 * answers included, its parent and root ids taken out), in the order they
 * were written. A record with no `lines` field reads nothing.
 */
export const mirroredLinesOfEntry = (schema: RecordSchemaResponse, entryId: string): EntryLinesWrite =>
  lineRunsOf(schema).length === 0 ? {} : heldLines(getDb() as unknown as DeviceTx, schema, entryId).lines;

/** Ids per read: well under SQLite's limit on bound parameters. */
const LINE_COUNT_CHUNK = 500;

/**
 * How many live lines each of these rows holds, per `lines` field: one read
 * per `lines` field for a whole page of rows, never one per row. A row that
 * holds none is absent from that field's map. A record with no `lines` field
 * reads nothing. The parent stores nothing for a `lines` field (Decision 4),
 * so a table's `line_count` cell asks this for the rows of the page it shows.
 */
export const lineCountsOf = (
  schema: RecordSchemaResponse,
  entryIds: readonly string[],
): Readonly<Record<string, ReadonlyMap<string, number>>> => {
  const out: Record<string, Map<string, number>> = {};
  if (entryIds.length === 0) return out;
  for (const run of lineRunsOf(schema)) {
    if (run.parentField === null) continue;
    const counts = new Map<string, number>();
    const path = rootPathOf(run.parentField);
    for (let at = 0; at < entryIds.length; at += LINE_COUNT_CHUNK) {
      const ids = entryIds.slice(at, at + LINE_COUNT_CHUNK);
      const rows = queryRaw<{ parentId: string | null; count: number }>(
        `SELECT json_extract(values_json, ?) AS parentId, COUNT(*) AS count
           FROM entry_mirror
          WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL
            AND json_extract(values_json, ?) IN (${ids.map(() => '?').join(', ')})
          GROUP BY parentId`,
        [path, run.recordId, path, ...ids],
      );
      for (const row of rows) if (row.parentId !== null) counts.set(row.parentId, Number(row.count));
    }
    out[run.linesFieldId] = counts;
  }
  return out;
};

/**
 * The lines an edit leaves the row with: each run it sends replaces the held
 * one whole, and a sent line keeps the held runs of its own that it does not
 * name. A run it does not send stays as held.
 */
const overLines = (held: EntryLinesWrite, sent: EntryLinesWrite): EntryLinesWrite => {
  const out: Record<string, readonly LineWrite[]> = { ...held };
  for (const [linesFieldId, run] of Object.entries(sent)) {
    const before = new Map((held[linesFieldId] ?? []).map((line) => [line.entryId, line]));
    out[linesFieldId] = run.map((line) => {
      const kept = before.get(line.entryId)?.lines ?? {};
      const lines = overLines(kept, line.lines ?? {});
      return Object.keys(lines).length === 0
        ? { entryId: line.entryId, values: line.values }
        : { entryId: line.entryId, values: line.values, lines };
    });
  }
  return out;
};

/**
 * Write a tree of worked-out lines into the mirror, each with its parent and
 * root ids, as pending rows. A line whose stored values are already these is
 * left alone, so an edit moves only the lines it changed.
 */
const writeLineRows = (
  tx: DeviceTx,
  schema: RecordSchemaResponse,
  lines: EntryLinesWrite,
  above: { parentId: string; rootId: string },
  tenant: string | null,
  now: string,
  stored: ReadonlyMap<string, string>,
): void => {
  for (const run of lineRunsOf(schema)) {
    for (const line of lines[run.linesFieldId] ?? []) {
      const values: Record<string, unknown> = { ...line.values };
      if (run.parentField !== null) values[run.parentField] = above.parentId;
      if (run.rootField !== null) values[run.rootField] = above.rootId;
      const valuesJson = JSON.stringify(values);
      if (stored.get(line.entryId) !== valuesJson) {
        const { day, signedValue } = materialize(run.schema, values as EntryValues, now);
        tx.insert(entryMirror)
          .values({
            entryId: line.entryId,
            recordId: run.recordId,
            tenantId: tenant,
            version: 0,
            versionId: null,
            serverSeq: null,
            isCurrent: true,
            deletedAt: null,
            day,
            signedValue,
            pending: true,
            valuesJson,
            createdBy: 'local',
            sourceChannel: 'typed',
            createdAt: now,
            updatedAt: now,
          })
          .onConflictDoUpdate({
            target: entryMirror.entryId,
            set: { day, signedValue, pending: true, deletedAt: null, valuesJson, updatedAt: now },
          })
          .run();
      }
      if (line.lines !== undefined) {
        writeLineRows(tx, run.schema, line.lines, { parentId: line.entryId, rootId: above.rootId }, tenant, now, stored);
      }
    }
  }
};

/** One run of lines: the row it hangs under and the lines record it is of. */
const runKey = (parentId: string, linesRecordId: string): string => `${parentId}|${linesRecordId}`;

/** Every run an edit's lines name, at every level, into `out`. */
const namedRunsOf = (
  schema: RecordSchemaResponse,
  parentId: string,
  lines: EntryLinesWrite,
  out: Set<string>,
  depth = 0,
): void => {
  if (depth >= LINES_MAX_DEPTH) return;
  for (const run of lineRunsOf(schema)) {
    const named = lines[run.linesFieldId];
    if (named === undefined) continue;
    out.add(runKey(parentId, run.recordId));
    for (const line of named) {
      if (line.lines !== undefined) namedRunsOf(run.schema, line.entryId, line.lines, out, depth + 1);
    }
  }
};

/**
 * Every lines record under a record, at both levels, by the ids its `lines`
 * fields name. Unlike `linesUnder`, a lines record with no schema on the
 * device is still named: its rows may have arrived before its schema did.
 */
const linesRecordsUnder = (schema: RecordSchemaResponse, depth = 0): string[] => {
  if (depth >= LINES_MAX_DEPTH) return [];
  return schema.fields.flatMap((field) => {
    if (field.type !== 'lines') return [];
    const spec = linesOf(field.spec);
    if (spec === null) return [];
    const inner = schema.lineSchemas?.[field.id] ?? schemaFor(spec.recordId);
    return [spec.recordId, ...(inner === null ? [] : linesRecordsUnder(inner, depth + 1))];
  });
};

/** Tombstone these lines and every line under them, as pending. */
const tombstoneLines = (tx: DeviceTx, run: LineRun, lines: readonly LineWrite[], now: string): void => {
  for (const line of lines) {
    tx.update(entryMirror)
      .set({ deletedAt: now, pending: true, updatedAt: now })
      .where(eq(entryMirror.entryId, line.entryId))
      .run();
    const under = line.lines ?? {};
    for (const inner of lineRunsOf(run.schema)) tombstoneLines(tx, inner, under[inner.linesFieldId] ?? [], now);
  }
};

/**
 * The lines an edit's sets leave out, at every level it names, tombstoned
 * with their own lines (Decision 5). A run it does not name is untouched.
 */
const tombstoneLeftOut = (
  tx: DeviceTx,
  schema: RecordSchemaResponse,
  held: EntryLinesWrite,
  sent: EntryLinesWrite,
  now: string,
): void => {
  for (const run of lineRunsOf(schema)) {
    const named = sent[run.linesFieldId];
    if (named === undefined) continue;
    const kept = new Map(named.map((line) => [line.entryId, line]));
    const before = held[run.linesFieldId] ?? [];
    tombstoneLines(tx, run, before.filter((line) => !kept.has(line.entryId)), now);
    for (const line of before) {
      const still = kept.get(line.entryId);
      if (still === undefined) continue;
      tombstoneLeftOut(tx, run.schema, line.lines ?? {}, still.lines ?? {}, now);
    }
  }
};

/**
 * The lines under one entry, at both levels, found by their root id: every
 * lines record under the entry's record shares the one path.
 */
const linesOfRoot = (
  tx: DeviceTx,
  entryId: string,
): { recordId: string; path: string }[] => {
  const row = tx
    .select({ recordId: entryMirror.recordId })
    .from(entryMirror)
    .where(eq(entryMirror.entryId, entryId))
    .get();
  if (row === undefined) return [];
  const schema = schemaFor(row.recordId);
  if (schema === null) return [];
  return linesUnder(schema, schemaFor).map((lines) => ({ recordId: lines.recordId, path: rootPathOf(lines.rootFieldId) }));
};

/**
 * A queued create taken back or refused: the lines it carried never reached
 * the server, so they leave the mirror with it. A line the server already
 * holds is not pending and stays, for the pull to settle.
 */
const forgetQueuedLines = (tx: DeviceTx, entryId: string): void => {
  for (const lines of linesOfRoot(tx, entryId)) {
    tx.delete(entryMirror)
      .where(
        and(
          eq(entryMirror.recordId, lines.recordId),
          eq(entryMirror.pending, true),
          sql`json_extract(${entryMirror.valuesJson}, ${lines.path}) = ${entryId}`,
        ),
      )
      .run();
  }
};

/** The entry's write landed: its lines are the server's now too. */
const settleQueuedLines = (tx: DeviceTx, entryId: string): void => {
  for (const lines of linesOfRoot(tx, entryId)) {
    tx.update(entryMirror)
      .set({ pending: false })
      .where(
        and(
          eq(entryMirror.recordId, lines.recordId),
          eq(entryMirror.pending, true),
          sql`json_extract(${entryMirror.valuesJson}, ${lines.path}) = ${entryId}`,
        ),
      )
      .run();
  }
};

/**
 * One line row as it stood before an edit wrote over it, or null for a line
 * the mirror did not hold (the edit made it). What a refusal puts back.
 */
type LineBefore = {
  readonly entryId: string;
  readonly row: {
    readonly valuesJson: string;
    readonly deletedAt: string | null;
    readonly day: string | null;
    readonly signedValue: number | null;
    readonly updatedAt: string;
  } | null;
};

/** Every line id in a tree, at every level. */
const lineIdsOf = (lines: EntryLinesWrite): string[] =>
  Object.values(lines).flatMap((run) => run.flatMap((line) => [line.entryId, ...lineIdsOf(line.lines ?? {})]));

/** These line rows as they stand now, before a write over them, a chunk of ids per read. */
const linesBeforeOf = (tx: DeviceTx, entryIds: readonly string[]): LineBefore[] => {
  const ids = [...new Set(entryIds)];
  const held = new Map<string, NonNullable<LineBefore['row']>>();
  for (let at = 0; at < ids.length; at += LINE_COUNT_CHUNK) {
    const rows = tx
      .select({
        entryId: entryMirror.entryId,
        valuesJson: entryMirror.valuesJson,
        deletedAt: entryMirror.deletedAt,
        day: entryMirror.day,
        signedValue: entryMirror.signedValue,
        updatedAt: entryMirror.updatedAt,
      })
      .from(entryMirror)
      .where(inArray(entryMirror.entryId, ids.slice(at, at + LINE_COUNT_CHUNK)))
      .all();
    for (const { entryId, ...row } of rows) held.set(entryId, row);
  }
  return ids.map((entryId) => ({ entryId, row: held.get(entryId) ?? null }));
};

/** An op's lines as they stood before it. Unreadable, or an op from before migration 15, reads as none. */
const readLinesBefore = (json: string | null | undefined): LineBefore[] => {
  if (json == null) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? (parsed as LineBefore[]) : [];
  } catch {
    return [];
  }
};

/**
 * A refused edit's lines, back as they stood before it: a line it made goes,
 * a line it changed or took away takes its stored row again. Only a row still
 * pending is touched: one a pull applied since is the server's already.
 */
const putLinesBack = (tx: DeviceTx, before: readonly LineBefore[]): void => {
  for (const one of before) {
    const still = and(eq(entryMirror.entryId, one.entryId), eq(entryMirror.pending, true));
    if (one.row === null) tx.delete(entryMirror).where(still).run();
    else tx.update(entryMirror).set({ ...one.row, pending: false }).where(still).run();
  }
};

/**
 * A refused edit with another of the entry's ops still queued: that op was
 * written over this one's lines, so what stood before the two is what stood
 * before this one. Its rows go to the next op, over that op's own, and the
 * last refusal puts them back.
 */
const passLinesBefore = (tx: DeviceTx, entryId: string, before: readonly LineBefore[]): void => {
  if (before.length === 0) return;
  const next = tx
    .select({ opId: outbox.opId, linesBeforeJson: outbox.linesBeforeJson })
    .from(outbox)
    .where(eq(outbox.entryId, entryId))
    .orderBy(asc(outbox.createdAt), asc(sql`rowid`))
    .limit(1)
    .get();
  if (next === undefined) return;
  const merged = new Map(readLinesBefore(next.linesBeforeJson).map((one) => [one.entryId, one]));
  for (const one of before) merged.set(one.entryId, one);
  tx.update(outbox)
    .set({ linesBeforeJson: JSON.stringify([...merged.values()]) })
    .where(eq(outbox.opId, next.opId))
    .run();
};

/**
 * Which business a write belongs to is not a guess: it is the business of the
 * RECORD it is going into, and that is a column away.
 *
 * Taken from the record rather than from the active pick, because the two can
 * differ for a moment — a switch lands its state before every screen has
 * unmounted — and an entry filed under the wrong business is money in the wrong
 * shop's day.
 *
 * The outbox op takes the same label as the mirror row: `push` sends an op
 * under the tenant it was called with, and the server refuses an op that names
 * another business's record. An op filed wrongly is a refusal, and a refusal is
 * final, which loses the entry.
 */
const tenantForWrite = (recordId: string, tenantId?: string | null): string | null =>
  tenantId ?? tenantOfRecord(recordId) ?? shownTenant();

/**
 * A form save: the entry row AND its outbox op, in one transaction, so the UI
 * is instant and the push can happen whenever the radio is up. The device
 * mints the entry id — the server is idempotent on it, so a retried push is
 * safe.
 */
export const insertPending = (
  recordId: string,
  typed: EntryValues,
  schema: RecordSchemaResponse,
  tenantId?: string | null,
  /**
   * The lines the form holds, keyed by `lines` field id, each with the id the
   * device made for it (Decision 5). The entry and every line land in the
   * mirror in this one transaction and leave in this one outbox op.
   */
  lines?: EntryLinesWrite,
  /** The id OS's card proposed a new row under (L64.1 of docs/plans/ledger.md); absent, the device makes one. */
  entryId?: string,
): PendingRow => {
  const tenant = tenantForWrite(recordId, tenantId);
  const now = new Date().toISOString();
  return getDb().transaction((tx) => writePending(tx, recordId, tenant, typed, schema, now, lines, entryId));
};

/**
 * A sheet save: every marked row of one day, in ONE transaction.
 *
 * The same three inserts per row that `insertPending` does — the mirror row,
 * the outbox op, the search document — over the same helper, so a mark written
 * from the sheet is indistinguishable from one written on the form. Decision 2
 * of `docs/plans/done/entry-sheet.md` is exactly this: one mark is one entry.
 *
 * One transaction and not N, for the reason a day of attendance is one gesture:
 * eight marks either land together or none of them do. A phone that died
 * between the fourth and the fifth would otherwise leave a half-marked day, and
 * reopening it would show four people marked and four not, with nothing on
 * screen to say which half was real.
 *
 * ONE `now` for the whole save, for the same reason: the eight rows were
 * recorded in one breath, and eight timestamps a millisecond apart would be a
 * precision this gesture does not have.
 *
 * The push groups them back up: `groupCreates` in `outbox-drain.ts` cuts
 * consecutive creates of one record into one batch call, so eight rows written
 * here leave as one request.
 */
export const insertPendingMany = (
  recordId: string,
  typed: readonly EntryValues[],
  schema: RecordSchemaResponse,
  tenantId?: string | null,
): PendingRow[] => {
  if (typed.length === 0) return [];
  const tenant = tenantForWrite(recordId, tenantId);
  const now = new Date().toISOString();
  return getDb().transaction((tx) =>
    typed.map((one) => writePending(tx, recordId, tenant, one, schema, now)),
  );
};

/**
 * A sheet re-marked: the row this device already holds, changed here first.
 *
 * The other half of Decision 11 of `docs/plans/done/entry-sheet.md`. A create is an
 * entry the server has never seen; an edit is a correction to one it has, and
 * until this existed a correction had to wait for the radio while the first
 * mark of the same day did not. Re-marking a day is the commonest thing that
 * happens on a sheet — somebody arrives late — so it is the one that must work
 * with no signal.
 *
 * Three things leave here, and the difference between them is the whole point.
 * The MIRROR takes the typed keys MERGED over what is stored, so a row whose
 * Hours and Note were filled on the form keeps them when its Status moves; the
 * merged whole then goes through `fillFormulas` and `materialize`, so the day
 * and the signed value on the row are the answers this device works out rather
 * than the ones it had. The OUTBOX carries ONLY the typed keys, because PATCH
 * is a merge on the server too, and re-sending every stored value would write a
 * field somebody else corrected in the meantime back to what this device last
 * saw. The DOCUMENT is re-rendered from the merged values, or search would
 * still answer with the old mark's words.
 *
 * `day` is materialized against the row's OWN `createdAt`, not this moment: an
 * entry whose record has no time axis falls back to when it was recorded, and
 * an edit does not move that.
 *
 * One op per call, never an update of the op that is already queued. Two edits
 * to one row are two PATCHes in the order they were made, which is what makes
 * the second one the one that stands; folding them together would be right only
 * as long as nothing else could have landed between them.
 */
export const updatePending = (
  recordId: string,
  entryId: string,
  typed: EntryValues,
  schema: RecordSchemaResponse,
  tenantId?: string | null,
  /**
   * The full set of each `lines` field the edit names, at any level
   * (Decision 5). A line the set leaves out is tombstoned with its own lines,
   * a new one is added, a changed one takes its new values. A `lines` field
   * the edit does not name keeps its lines.
   */
  lines?: EntryLinesWrite,
): PendingRow => {
  const tenant = tenantForWrite(recordId, tenantId);
  const now = new Date().toISOString();
  const opId = newLocalId();
  return getDb().transaction((tx) => {
    const before = tx
      .select()
      .from(entryMirror)
      .where(eq(entryMirror.entryId, entryId))
      .limit(1)
      .all()[0];
    // Nothing to correct. Thrown rather than shrugged at, because the caller
    // read this row off this device a moment ago and a silent no-op would show
    // a mark that moved on screen and nowhere else.
    if (before === undefined) {
      throw new Error('That entry is not on this device yet. Connect once, then try again.');
    }

    const stored = JSON.parse(before.valuesJson) as EntryValues;
    const merged = { ...stored, ...typed };
    // The totals of the row read every line under it, the stored ones as well
    // as the ones this edit sends, so the tree is the mirror's with the sent
    // runs laid over it.
    const runs = lineRunsOf(schema);
    const held = runs.length === 0 ? null : heldLines(tx, schema, entryId);
    const tree =
      held === null ? null : fillFormulaTree(schema, { entryId, values: merged }, overLines(held.lines, lines ?? {}));
    const values = tree === null ? fillFormulas(schema, merged) : tree.values;
    // Must be, as on a create (F13, F14); an entry saved before a rule was
    // made must keep it once it is edited (F16). Only the lines this edit
    // sends are its write.
    holdRules(schema, { entryId, values }, tree === null ? null : tree.lines, undefined, new Set(lineIdsOf(lines ?? {})));
    const pushed = withoutComputed(schema, typed);
    const { day, signedValue } = materialize(schema, values, before.createdAt);
    keepShownPicks(tx, schema, typed);
    // Every line this write may move — each one held, each one it sends — as
    // it stands now, kept on the op so a refusal can put it back (`dropEditOp`).
    let linesBefore: LineBefore[] | null = null;
    if (tree !== null && held !== null) {
      linesBefore = linesBeforeOf(tx, [...held.json.keys(), ...lineIdsOf(tree.lines)]);
      writeLineRows(tx, schema, tree.lines, { parentId: entryId, rootId: entryId }, tenant, now, held.json);
      tombstoneLeftOut(tx, schema, held.lines, lines ?? {}, now);
    }

    tx.update(entryMirror)
      .set({
        day,
        signedValue,
        pending: true,
        valuesJson: JSON.stringify(values),
        updatedAt: now,
      })
      .where(eq(entryMirror.entryId, entryId))
      .run();
    tx.insert(outbox)
      .values({
        opId,
        kind: 'edit',
        recordId,
        tenantId: tenant,
        entryId,
        valuesJson: JSON.stringify(pushed),
        linesJson: lines === undefined ? null : JSON.stringify(withoutComputedLines(schema, lines)),
        linesBeforeJson: linesBefore === null || linesBefore.length === 0 ? null : JSON.stringify(linesBefore),
        fieldSetVer: schema.fieldSetVer,
        failedReason: null,
        createdAt: now,
      })
      .run();
    // SEAM 1b of 8, beside the create's. The values moved, so the words did.
    indexEntry(schema, entryId, recordId, day, values);

    return {
      entryId,
      version: before.version,
      values,
      createdAt: before.createdAt,
      updatedAt: now,
      createdBy: before.createdBy ?? 'local',
      sourceChannel: before.sourceChannel ?? 'typed',
      linkId: before.linkId ?? null,
      sourceEntryId: before.sourceEntryId ?? null,
      pending: true,
    };
  });
};

/**
 * How many ops of one entry are still queued behind the one just retired.
 *
 * `pending` is one flag over however many ops an entry has, so it may only be
 * cleared by the LAST of them. Two edits queued on one row — a mark moved, then
 * moved again — must both reach the server, and clearing the flag on the first
 * ack would tell the screen the second one had landed too.
 */
const opsLeftFor = (tx: DeviceTx, entryId: string): number =>
  tx
    .select({ n: sql<number>`count(*)` })
    .from(outbox)
    .where(eq(outbox.entryId, entryId))
    .get()?.n ?? 0;

/**
 * The server accepted an op: stamp the real ids on the row, retire the op.
 *
 * SEAM 3 of 8, and it is deliberately EMPTY. Nothing here touches values, the
 * record or the day, so the document is still correct. Written down because an
 * unremarked gap in a list of eight reads like an oversight.
 */
export const markSynced = (
  entryId: string,
  ack: { version: number; versionId: string; serverSeq: number },
): void => {
  const now = new Date().toISOString();
  getDb().transaction((tx) => {
    tx.update(entryMirror)
      .set({
        version: ack.version,
        versionId: ack.versionId,
        serverSeq: ack.serverSeq,
        pending: false,
        updatedAt: now,
      })
      .where(eq(entryMirror.entryId, entryId))
      .run();
    tx.delete(outbox).where(eq(outbox.entryId, entryId)).run();
    // The lines landed in the same server transaction as their entry.
    settleQueuedLines(tx, entryId);
  });
};

/**
 * A server refusal is final: drop the pending row AND the op, so the list
 * does not show a ghost that will never land.
 */
export const removePending = (entryId: string): void => {
  getDb().transaction((tx) => {
    // The lines it carried never landed either. Taken first: they are found
    // through their entry's record, which the row about to go names.
    forgetQueuedLines(tx, entryId);
    tx.delete(entryMirror)
      .where(and(eq(entryMirror.entryId, entryId), eq(entryMirror.pending, true)))
      .run();
    tx.delete(outbox).where(eq(outbox.entryId, entryId)).run();
    // SEAM 2 of 8. The row is gone, so its document must go with it or search
    // would offer a hit that opens nothing.
    forgetDoc(entryId);
  });
};

/**
 * Take back an entry the server has never seen: its create still waits in the
 * outbox. True when it did, and the row, its op and its search document are
 * gone; false when there is no queued create for it, so the entry is the
 * server's and a DELETE there is the way to remove it.
 *
 * A card's Undo right after a save is the case (a DELETE for an entry that
 * never landed is a 404). A push already on the wire can still land it; the
 * next pull then brings it back, which is what the server holds.
 *
 * An entry's lines go with it (Decision 19 of
 * docs/plans/entry-lines-and-layout.md): a create carries its lines in the
 * same outbox op, so dropping the op drops them, and a line the server
 * already holds is tombstoned there with its entry and arrives by the pull.
 */
export const forgetQueuedCreate = (entryId: string): boolean => {
  const queued = getDb()
    .select({ opId: outbox.opId })
    .from(outbox)
    .where(and(eq(outbox.entryId, entryId), eq(outbox.kind, 'create')))
    .limit(1)
    .all()[0];
  if (queued === undefined) return false;
  removePending(entryId);
  return true;
};

/**
 * Where one entry stands on this phone: `queued` while an op for it waits in
 * the outbox, `synced` when its row is here and no longer pending, and
 * undefined when this phone holds neither.
 *
 * The last is not "synced". A create the server refused leaves the outbox too
 * (`removePending` takes its op and its row), and it never reached the
 * server. A card's receipt reads this to say `pendingSync` (C6 of
 * docs/plans/setup-coach-build.md), so only a row this phone holds as synced
 * may clear it.
 */
export const entrySyncState = (entryId: string): 'queued' | 'synced' | undefined => {
  const db = getDb();
  const op = db.select({ opId: outbox.opId }).from(outbox).where(eq(outbox.entryId, entryId)).limit(1).all()[0];
  if (op !== undefined) return 'queued';
  const row = db
    .select({ pending: entryMirror.pending })
    .from(entryMirror)
    .where(and(eq(entryMirror.entryId, entryId), eq(entryMirror.isCurrent, true)))
    .limit(1)
    .all()[0];
  if (row === undefined) return undefined;
  return row.pending ? 'queued' : 'synced';
};

/**
 * The server accepted ONE op of an entry: stamp the ids, retire that op alone.
 *
 * The edit's answer to `markSynced`, and the difference is which rows the
 * delete names. `markSynced` retires every op of the entry, which is exactly
 * right for a create — a create is the FIRST op an entry has, so anything
 * queued behind it was written against a row the server had not seen and is
 * carried by the create's own values. An edit is not first: an op behind it is
 * a second correction somebody made, and deleting it here would lose a change
 * they watched go grey.
 *
 * So the flag comes off only when nothing is left. Until then the row stays
 * pending with the server's version on it, which is the truth: this device
 * holds a change the server does not.
 */
export const markSyncedOp = (
  opId: string,
  entryId: string,
  ack: { version: number; versionId: string; serverSeq: number },
): void => {
  const now = new Date().toISOString();
  getDb().transaction((tx) => {
    tx.delete(outbox).where(eq(outbox.opId, opId)).run();
    const settled = opsLeftFor(tx, entryId) === 0;
    if (settled) settleQueuedLines(tx, entryId);
    tx.update(entryMirror)
      .set({
        version: ack.version,
        versionId: ack.versionId,
        serverSeq: ack.serverSeq,
        ...(settled ? { pending: false } : {}),
        updatedAt: now,
      })
      .where(eq(entryMirror.entryId, entryId))
      .run();
  });
};

/**
 * The server refused an edit: drop the op and LEAVE THE VALUES.
 *
 * `removePending` is wrong here and it is worth saying why, because the two
 * words are one letter apart. A refused create is a row the server does not
 * have and never will, so deleting it takes away a ghost. A refused edit is a
 * row the server DOES have: deleting it would take away an entry somebody
 * recorded successfully, over a correction to it that was refused.
 *
 * The entry's own values are not restored. The values on the row are what this
 * device believed a moment ago and the server's are what is true; putting the
 * old ones back would need a copy of them kept somewhere, and there is a copy
 * already — on the server, one pull away. The op is gone, so `applyPulled`
 * stops skipping this row, and the next page corrects it.
 *
 * Its LINES are put back, from the copy the op kept (`linesBeforeJson`). They
 * are rows of their own that the edit wrote over — a line it changed, one it
 * took away, one it made — and no page will say otherwise: the server holds
 * what it held, and the cursor is past it. Left alone they would stay pending
 * for ever with values the server refused. With an op still queued behind
 * this one the copy passes to it instead, and the last answer settles them.
 */
export const dropEditOp = (opId: string, entryId: string): void => {
  getDb().transaction((tx) => {
    const op = tx.select({ linesBeforeJson: outbox.linesBeforeJson }).from(outbox).where(eq(outbox.opId, opId)).get();
    tx.delete(outbox).where(eq(outbox.opId, opId)).run();
    const linesBefore = readLinesBefore(op?.linesBeforeJson);
    if (opsLeftFor(tx, entryId) > 0) {
      passLinesBefore(tx, entryId, linesBefore);
      return;
    }
    putLinesBack(tx, linesBefore);
    // A line an answered op wrote and this one did not is the server's.
    settleQueuedLines(tx, entryId);
    tx.update(entryMirror)
      .set({ pending: false })
      .where(eq(entryMirror.entryId, entryId))
      .run();
    // The entry's words fold its lines as they stand again (SEAM 1b's undo).
    if (linesBefore.length > 0) refoldEntry(entryId, true);
  });
};

/* ------------------------------------------------- the worked-out-field nudge */

/**
 * One key per record in `sync_meta`, so a dismissal needs no table of its own.
 *
 * `sync_meta` already carries this shape of fact — a small flag about one
 * record, written once and read on every open. A table for one boolean would be
 * a migration and a maintenance path for nothing.
 */
const nudgeKey = (recordId: string): string => `formula_nudge_off:${recordId}`;

/**
 * Has this record's offer been turned down?
 *
 * "Not now" means not ever, on this record. A nudge that comes back is an
 * advertisement, which is the fourth of the four rules on artboard 11.
 */
export const formulaNudgeIsOff = (recordId: string): boolean =>
  getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, nudgeKey(recordId)))
    .limit(1)
    .all().length > 0;

/** Turn it down. Written on the tap, so a crash cannot bring the card back. */
export const turnOffFormulaNudge = (recordId: string): void => {
  getDb()
    .insert(syncMeta)
    .values({ key: nudgeKey(recordId), value: '1' })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value: '1' } })
    .run();
};

/* ------------------------------------------------------- the insight offer */

/**
 * Has this record's offer of three insights been turned down?
 *
 * "Not now" means not again on this record, the way the formula nudge's does:
 * an offer that comes back after a no is an advertisement. The single row
 * action takes its place, so there is still one way to add an insight — the
 * offer is a teacher, not the door.
 *
 * Per record and per device, and deliberately not in `layout_json`: one
 * person's no is not the shop's decision.
 */
const offerKey = (recordId: string): string => `insight_offer_off:${recordId}`;

export const insightOfferIsOff = (recordId: string): boolean =>
  getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, offerKey(recordId)))
    .limit(1)
    .all().length > 0;

/** Turn it down, on the tap, so a crash cannot bring it back. */
export const turnOffInsightOffer = (recordId: string): void => {
  getDb()
    .insert(syncMeta)
    .values({ key: offerKey(recordId), value: '1' })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value: '1' } })
    .run();
};

/* ------------------------------------------- insight edit mode's coach */

/**
 * Whether this phone has been shown how edit mode works.
 *
 * The coach line — "Drag the dots to move an insight, and its corner to change
 * its size." — is said the first time anybody arranges insights on this
 * phone, on any record, and then never again. Device-wide, like the reason it
 * exists: learning the gesture is a person's, not a record's.
 */
const COACHED_KEY = 'insight_edit_coached';

export const insightEditCoached = (): boolean =>
  getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, COACHED_KEY)).limit(1).all()
    .length > 0;

/** Written as the line is first shown, so a crash cannot make it repeat. */
export const markInsightEditCoached = (): void => {
  getDb()
    .insert(syncMeta)
    .values({ key: COACHED_KEY, value: '1' })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value: '1' } })
    .run();
};

/* -------------------------------------------------- the insight grid's fold */

/**
 * Whether this record's insight section is unfolded, per record and per
 * device — the same shape of fact as the nudge above, and kept for the same
 * reason: a small flag about one record, read on every open.
 *
 * It is deliberately NOT part of `layout_json`. The layout is what the record
 * is asked; the fold is how far one person on one phone has opened it, and a
 * layout that carried it would sync somebody's scroll position to everybody.
 */
const foldKey = (recordId: string): string => `insight_fold:${recordId}`;

export const insightFoldIsOpen = (recordId: string): boolean =>
  getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, foldKey(recordId)))
    .limit(1)
    .all()
    .some((row) => row.value === '1');

/** Remember it on the tap, so a crash cannot fold a section somebody opened. */
export const setInsightFoldOpen = (recordId: string, open: boolean): void => {
  const value = open ? '1' : '0';
  getDb()
    .insert(syncMeta)
    .values({ key: foldKey(recordId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/**
 * One business's outbox in drain order: oldest first, rowid breaking ties.
 *
 * **The tenant predicate here is a data-loss fix, not a tidiness one.** `push`
 * sends every op it is handed under the tenant id it was called with. An op
 * belonging to another business is refused with a 4xx; `mayDropOp` reads a 4xx
 * as final and drops the op AND its pending row. That is somebody's unsent sale
 * deleted by another business's push. It is also the reason `forgetDevice` has
 * always had to wipe everything at sign-out.
 *
 * So when this cannot tell whose ops these are — more than one business here and
 * a caller that named none — it hands back NOTHING. A push that sends nothing
 * leaves every op where it is for the next trigger; a push that sends the wrong
 * ones cannot be undone.
 */
export const listOutbox = (tenantId?: string | null): OutboxOp[] => {
  const { tenant, ambiguous } = syncScope(tenantId);
  if (ambiguous) return [];
  return getDb()
    .select()
    .from(outbox)
    .where(sameTenant(outbox.tenantId, tenant))
    .orderBy(asc(outbox.createdAt), asc(sql`rowid`))
    .all()
    .map((row) => ({
      opId: row.opId,
      kind: row.kind,
      recordId: row.recordId,
      entryId: row.entryId,
      values: JSON.parse(row.valuesJson) as EntryValues,
      ...(row.linesJson === null ? {} : { lines: JSON.parse(row.linesJson) as EntryLinesWrite }),
      fieldSetVer: row.fieldSetVer,
      createdAt: row.createdAt,
    }));
};

/** How many writes are waiting, for the business whose badge is on screen. */
export const outboxCount = (tenantId?: string | null): number => {
  const row = getDb()
    .select({ n: sql<number>`count(*)` })
    .from(outbox)
    .where(sameTenant(outbox.tenantId, shownTenant(tenantId)))
    .get();
  return row?.n ?? 0;
};

/* -------------------------------------------------------------- pull apply */

const sinceKey = (tenantId: string): string => `since:${tenantId}`;

/** The cursor for one tenant: the highest server_seq this device applied. */
export const getSince = (tenantId: string): number => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, sinceKey(tenantId)))
    .get();
  const parsed = row === undefined ? Number.NaN : Number(row.value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The schemas as pulled, followed by every lines record's schema nested in
 * them, at both levels. The pull never sends a lines record's schema on its
 * own (it is not in the catalog): it rides in its parent's `lineSchemas`
 * (docs/plans/entry-lines-and-layout.md, Decision 19). Each is stored under
 * its own record id, so `schemaFor` answers a line's record as it answers any
 * other, and the search folds a line's words into its root entry.
 */
const withLineSchemas = (schemas: readonly RecordSchemaResponse[], depth = 0): RecordSchemaResponse[] =>
  schemas.flatMap((schema) => [
    schema,
    ...(depth >= LINES_MAX_DEPTH ? [] : withLineSchemas(Object.values(schema.lineSchemas ?? {}), depth + 1)),
  ]);

/**
 * Store pulled schemas, rebuilding the insight view of any record whose field
 * set changed. Schemas land BEFORE the entries that read them — the pull page
 * carries the changed ones, and materialization at ingest reads them from here.
 *
 * Answers the records whose field set changed here, so the sync can wake a
 * form already open on one: a field order saved on another device, or one this
 * browser saved before a reload lost its copy, reaches the screen.
 */
export const cacheSchemas = (schemas: RecordSchemaResponse[]): readonly string[] => {
  if (schemas.length === 0) return [];
  const db = getDb();
  const changed: RecordSchemaResponse[] = [];
  const firstSeen = new Set<string>();
  db.transaction((tx) => {
    for (const schema of withLineSchemas(schemas)) {
      const held = tx
        .select({ fieldSetVer: schemaCache.fieldSetVer })
        .from(schemaCache)
        .where(eq(schemaCache.recordId, schema.recordId))
        .get();
      const schemaJson = JSON.stringify(schema);
      tx.insert(schemaCache)
        .values({ recordId: schema.recordId, fieldSetVer: schema.fieldSetVer, schemaJson })
        .onConflictDoUpdate({
          target: schemaCache.recordId,
          set: { fieldSetVer: schema.fieldSetVer, schemaJson },
        })
        .run();
      if (held?.fieldSetVer !== schema.fieldSetVer) {
        changed.push(schema);
        if (held === undefined) firstSeen.add(schema.recordId);
      }
    }
  });
  for (const schema of changed) {
    // A lines record has no insight view, no list and no documents of its
    // own (Decision 19): its words are in its root entry's document, and the
    // parent's field set moves with it, which is what marks those stale.
    if (isLinesSchema(schema)) continue;
    rebuildView(schema, execRaw);
    // A field added, retyped or removed moves the record's grouped-field
    // indexes with it. Only the difference runs: an index still wanted stays.
    for (const statement of fieldIndexChanges(schema, fieldIndexesOf(schema.recordId))) execRaw(statement);
    // A schema seen for the FIRST time, with nothing of that record on the
    // device yet, has no documents to go stale: every row that follows it is
    // indexed as it lands (seam 5), because the schema is now in hand. So the
    // record is stamped current rather than marked stale. Marking it stale
    // made a first sync index every row twice — once on ingest, then again in
    // the backfill — and on a business of five lakh entries that second pass
    // held the JavaScript thread for minutes after the pull had finished.
    // Rows that arrived BEFORE their schema were already marked stale by
    // seam 5, and a row held here keeps this record on the stale path.
    if (firstSeen.has(schema.recordId) && !hasMirroredRows(schema.recordId)) {
      stampSearch(schema.recordId);
      continue;
    }
    // SEAM 6 of 8. A changed field set changes what every document of this
    // record should say: an option relabelled, a title binding moved, a field
    // added.
    //
    // MARKED, not rebuilt. This runs inside a pull, and rebuilding fifty
    // thousand documents here would freeze the screen mid-sync. The backfill
    // driver picks the record up on the next pass and yields between chunks.
    // Until it does, the old documents stay searchable — stale words being far
    // better than none.
    markSearchStale(schema.recordId);
  }
  return changed.map((schema) => schema.recordId);
};

/**
 * Apply one pulled page and advance the cursor, in ONE transaction: entries
 * without the cursor would be re-fetched (harmless, the upserts are
 * idempotent), but a cursor without its entries would skip them forever.
 *
 * Rows arrive in server_seq order and are applied in it. A current tombstone
 * sets deleted_at; a current live row upserts over whatever is mirrored —
 * including a pending row, which is how a pull acks what the push answer has
 * not reached yet. A non-current row is history and is ignored; the cursor
 * advances past it all the same.
 *
 * ONE row is not applied: one whose entry still has an `edit` op waiting in the
 * outbox. See the comment at the skip below.
 *
 * Returns the record ids whose mirror changed, so the engine can bump them.
 */
/**
 * The upsert `applyPulled` runs once per pulled row, as SQL.
 *
 * Column for column the Drizzle call it replaces: every column is written on
 * the insert and again on the update, a pulled row is current, not deleted
 * and not pending, and updated_at is the version's own createdAt.
 *
 * The last parameter is 1 for a line: a line keeps the earliest created_at
 * it was ever given, because a run of lines is read in that order
 * (`rowsOfRun`), as the server reads it by each line's first version. An
 * entry takes the time it was first added (`addedAt`), or its version's time
 * from a server that does not send one.
 */
const UPSERT_PULLED_SQL = `
  INSERT INTO entry_mirror (
    entry_id, record_id, tenant_id, version, version_id, server_seq,
    is_current, deleted_at, day, signed_value, pending, values_json,
    created_by, source_channel, link_id, source_entry_id, created_at, updated_at
  ) VALUES (?, ?, ?, ?, ?, ?, 1, NULL, ?, ?, 0, ?, ?, ?, ?, ?, ?, ?)
  ON CONFLICT(entry_id) DO UPDATE SET
    record_id = excluded.record_id,
    tenant_id = excluded.tenant_id,
    version = excluded.version,
    version_id = excluded.version_id,
    server_seq = excluded.server_seq,
    is_current = 1,
    deleted_at = NULL,
    day = excluded.day,
    signed_value = excluded.signed_value,
    pending = 0,
    values_json = excluded.values_json,
    created_by = excluded.created_by,
    source_channel = excluded.source_channel,
    link_id = excluded.link_id,
    source_entry_id = excluded.source_entry_id,
    created_at = CASE WHEN ? = 1 THEN min(entry_mirror.created_at, excluded.created_at) ELSE excluded.created_at END,
    updated_at = excluded.updated_at`;

export const applyPulled = (
  rows: SyncEntryRow[],
  nextSince: number,
  tenantId: string,
  /**
   * False during a long first download. A search document is about four of
   * the five writes a row costs, so the rows go in alone, their records are
   * marked stale, and `runSearchBackfill` builds the documents in one pass
   * when the download ends. The search screen says so meanwhile.
   */
  options: { readonly index?: boolean } = {},
): string[] => {
  const index = options.index ?? true;
  const ordered = [...rows].sort((a, b) => a.serverSeq - b.serverSeq);
  const schemas = new Map<string, RecordSchemaResponse | null>();
  for (const row of ordered) {
    if (!schemas.has(row.recordId)) schemas.set(row.recordId, schemaFor(row.recordId));
  }

  /**
   * The entries this device still owes an edit for.
   *
   * Read whole rather than cut to this page's ids: the outbox holds only what
   * this device has not managed to send, which is a handful of rows, while a
   * page of entries is hundreds — and hundreds of bound parameters is a limit
   * SQLite actually has.
   */
  const edits = getDb()
    .select({ entryId: outbox.entryId, recordId: outbox.recordId, linesJson: outbox.linesJson })
    .from(outbox)
    .where(eq(outbox.kind, 'edit'))
    .all();
  const editing = new Set(edits.map((row) => row.entryId));
  /**
   * The runs of lines those edits name, as `runKey(parent, lines record)`.
   * An edit sends the full set of each run it names, so a pulled line in such
   * a run is older than what this device will send. A run no edit names is
   * not: a line another phone added there must land, or the cursor moves past
   * it and the next save from this device sends a set without it.
   */
  const namedRuns = new Set<string>();
  for (const edit of edits) {
    if (edit.linesJson === null) continue;
    const schema = schemaFor(edit.recordId);
    try {
      const lines = JSON.parse(edit.linesJson) as EntryLinesWrite;
      if (schema !== null) namedRunsOf(schema, edit.entryId, lines, namedRuns);
    } catch {
      // An op whose lines cannot be read names no run here; its push answers for it.
    }
  }

  const touched = new Set<string>();
  /** Records whose entries arrived before any schema for them did. */
  const stale = new Set<string>();
  /**
   * The entries whose lines this page moved (Decision 19). A line holds no
   * document; its words are in its root entry's, which is written again once
   * the whole page is in, so a root and its lines on one page fold once.
   */
  const refold = new Set<string>();
  /** The line schema of a row, when its record is a lines record the device holds a schema for. */
  const lineSchemaOf = (recordId: string): RecordSchemaResponse | null => {
    const schema = schemas.get(recordId) ?? null;
    return schema !== null && isLinesSchema(schema) ? schema : null;
  };
  /**
   * When each line on this page was first written, from its earliest version
   * here: the page carries a line's old versions too, before its current one,
   * and only the first says where the line sits in its run.
   */
  const firstAt = new Map<string, string>();
  for (const row of ordered) {
    if (lineSchemaOf(row.recordId) === null) continue;
    const seen = firstAt.get(row.entryId);
    if (seen === undefined || row.createdAt < seen) firstAt.set(row.entryId, row.createdAt);
  }
  getDb().transaction((tx) => withPrepared(UPSERT_PULLED_SQL, (upsertPulled) => {
    for (const row of ordered) {
      if (!row.isCurrent) continue;
      touched.add(row.recordId);

      if (row.deleted) {
        // A line's tombstone carries no values, so its root is read off the
        // row it replaces, before that row is overwritten.
        const lineSchema = lineSchemaOf(row.recordId);
        if (lineSchema !== null) {
          const held = tx
            .select({ valuesJson: entryMirror.valuesJson })
            .from(entryMirror)
            .where(eq(entryMirror.entryId, row.entryId))
            .get();
          try {
            const root = held === undefined ? null : rootOfLine(lineSchema, JSON.parse(held.valuesJson) as EntryValues);
            if (root !== null) refold.add(root);
          } catch {
            // Unreadable values name no root; the root's own next write folds again.
          }
        }
        // A picked entry removed is named no more, held here or not: the title a pull or a prune kept goes with it (step 9.8).
        tx.delete(pickTitle).where(eq(pickTitle.entryId, row.entryId)).run();
        // The tombstone carries the version's own timestamp, not the device's
        // clock: createdAt of a tombstone version is when the delete happened.
        tx.update(entryMirror)
          .set({
            version: row.version,
            versionId: row.versionId,
            serverSeq: row.serverSeq,
            pending: false,
            deletedAt: row.createdAt,
            updatedAt: row.createdAt,
          })
          .where(eq(entryMirror.entryId, row.entryId))
          .run();
        // SEAM 4 of 8. A deleted entry must stop being findable at once.
        forgetDoc(row.entryId);
        continue;
      }

      /*
       * A row with an unsent edit behind it keeps this device's values.
       *
       * The person changed them AFTER this version was written, so the page is
       * the older of the two answers and applying it would flip a mark back on
       * screen — and then flip it again when the push is answered. The ack
       * settles it instead: `markSyncedOp` stamps the version the edit
       * produced. A refusal drops the op, and the page after that applies
       * normally, which is how the row gets corrected.
       *
       * A TOMBSTONE still wins, and it is handled above this line: an entry
       * somebody deleted has nothing left to correct.
       */
      if (editing.has(row.entryId)) continue;
      // A line in a run an unsent edit names keeps this device's set for the
      // same reason: that edit carries the full run, and the ack or the
      // refusal settles it. A run the edit does not name is applied.
      const pulledLine = lineSchemaOf(row.recordId);
      if (pulledLine !== null) {
        const parentField = lineRefFields(pulledLine).parent;
        const parent = parentField === null ? null : row.values[parentField];
        if (typeof parent === 'string' && namedRuns.has(runKey(parent, row.recordId))) continue;
      }

      const { day, signedValue } = materialize(
        schemas.get(row.recordId) ?? null,
        row.values,
        row.createdAt,
      );
      // The page was pulled FOR this business, so every row on it is this
      // business's. Written on the update as well as the insert: a device
      // that upgraded with rows it could not label (see migration 6) has
      // them labelled by the next pull that carries them.
      //
      // Through one prepared statement for the whole call, not Drizzle: see
      // `withPrepared` for the leak that a first download turned into a kill.
      upsertPulled([
        row.entryId,
        row.recordId,
        tenantId,
        row.version,
        row.versionId,
        row.serverSeq,
        day,
        signedValue,
        JSON.stringify(row.values),
        row.createdBy,
        row.sourceChannel,
        row.linkId ?? null,
        row.sourceEntryId ?? null,
        // An entry is added once: a later version (an edit, a room bill an
        // order moved) keeps the first time, which the server sends as addedAt.
        pulledLine === null ? (row.addedAt ?? row.createdAt) : (firstAt.get(row.entryId) ?? row.createdAt),
        row.createdAt,
        pulledLine === null ? 0 : 1,
      ]);

      // SEAM 5 of 8. The pull page carries changed schemas BEFORE the entries
      // that read them, so one is normally in hand. When it is not — a record
      // this device has never held a schema for — the document cannot be
      // rendered at all, so the record is marked stale and the backfill returns
      // for it. Leaving it silently unindexed is the drift to avoid.
      const schema = schemas.get(row.recordId) ?? null;
      if (schema !== null && isLinesSchema(schema)) {
        // A line: never a document of its own (Decision 19). Its root entry
        // is written again after the page, with this line's words in it.
        forgetDoc(row.entryId);
        const root = rootOfLine(schema, row.values);
        if (root !== null) refold.add(root);
      } else if (schema === null) {
        stale.add(row.recordId);
        forgetDoc(row.entryId);
      } else if (!index) {
        // The old document, if any, stays until the backfill writes over it;
        // the stale mark is what brings the backfill back to this record.
        stale.add(row.recordId);
      } else {
        indexEntry(schema, row.entryId, row.recordId, day, row.values);
      }
    }

    // The roots whose lines moved, each written once, with every line the
    // mirror now holds. During a long first download the root's record is
    // marked stale instead, and the backfill folds its lines with the rest.
    for (const root of refold) {
      const folded = refoldEntry(root, index);
      if (folded !== null && !folded.written) stale.add(folded.recordId);
    }

    tx.insert(syncMeta)
      .values({ key: sinceKey(tenantId), value: String(nextSince) })
      .onConflictDoUpdate({ target: syncMeta.key, set: { value: String(nextSince) } })
      .run();
  }));
  // Outside the transaction: clearing a stamp is not part of what must roll
  // back with the page, and a stamp cleared twice costs nothing.
  for (const recordId of stale) markSearchStale(recordId);
  return [...touched];
};

/**
 * The catalog half of a pull page. field_set_ver is NOT written here:
 * RecordListItem does not carry it — schema_cache owns it, and the pull
 * request reads its schemaVersions from there.
 *
 * **Pass the tenant of the page.** A catalog row is what every other
 * business-wide read is scoped through, so a row filed under the wrong business
 * shows one shop's records on another's home screen. Called with nothing on a
 * phone holding more than one business, this cannot know whose page it is — the
 * cursor it would read is already advanced for both — so a NEW row is written
 * with no business at all, and an EXISTING row keeps the one it has. Unlabelled
 * means invisible until the next pull that names its business, which is the
 * failure worth having.
 *
 * **Returns whether the mirror changed**, and that is the engine's only way to
 * tell. The server sends the WHOLE readable catalog on every pull (item 2 of
 * `apps/server/src/routes/sync.ts`), so a page carrying records is not news.
 * The engine used to read `records.length > 0` as "something landed", and every
 * quiet 45s pull then re-stamped the business and bumped the catalog: the record
 * page and the Dashboard re-read everything and re-rendered twice each, on every
 * tick, with nothing on screen different (measured on both devices, 23 Sep
 * 2026). So a row is compared with the one stored — column by column, the same
 * columns the write sets — and only a new row, a different one, or rows the
 * grant took away count. A row that is the same is not written again either:
 * writing exactly what is there is the one write that cannot matter.
 */
export const upsertCatalog = (records: RecordListItem[], tenantId?: string | null): boolean => {
  if (records.length === 0) return false;
  const { tenant } = syncScope(tenantId);
  let changed = false;
  getDb().transaction((tx) => {
    const stored = new Map(
      tx
        .select()
        .from(recordCatalog)
        .where(
          inArray(
            recordCatalog.id,
            records.map((one) => one.id),
          ),
        )
        .all()
        .map((row) => [row.id, row] as const),
    );
    for (const record of records) {
      const columns = catalogColumnsOf(record);
      const before = stored.get(record.id);
      // Only when this page said whose it is: an update that wrote a null here
      // would un-label a record that was already labelled correctly. So a page
      // that cannot say is not a difference in the label either.
      const relabel = tenant !== null && before !== undefined && before.tenantId !== tenant;
      if (before !== undefined && !relabel && !differs(before, columns)) continue;
      changed = true;
      tx.insert(recordCatalog)
        .values({ id: record.id, tenantId: tenant, ...columns })
        .onConflictDoUpdate({
          target: recordCatalog.id,
          set: { ...(tenant === null ? {} : { tenantId: tenant }), ...columns },
        })
        .run();
    }
    if (pruneUnreadableRows(tx, records)) changed = true;
  });
  return changed;
};

/**
 * Every column a pull writes onto a catalog row except its id and business —
 * one object for the insert, the update and the comparison, so the three cannot
 * disagree about what a row is.
 */
const catalogColumnsOf = (record: RecordListItem) => ({
  name: record.name,
  kind: record.kind,
  nounKey: record.nounKey,
  entryNounKey: record.entryNounKey,
  currency: record.currency,
  direction: record.direction,
  // Written on every pull that changes the row, null included. An icon taken
  // off in Customize is answered as an absent key, and a row that skipped the
  // write on a falsy value would keep drawing the old glyph.
  icon: record.icon ?? null,
  entryCount: record.entryCount,
  headlineValue: record.headlineValue,
  lastEntryAt: record.lastEntryAt,
  // A list that stops being quiet is written back as null, so it shows again (L65).
  quiet: record.quiet === true ? true : null,
  // Null included, for the same reason. A REVOKED capability is a null or a
  // shorter list, so skipping the write on a falsy value would leave a person
  // holding a key the server took back.
  ...packGrant(record.grant),
});

/**
 * Whether any written column differs from the stored row. An absent value on
 * the wire is stored as null, so it is compared as null — otherwise an older
 * server's payload would read as a change on every pull, which is the defect
 * this exists to end.
 */
const differs = (stored: Record<string, unknown>, columns: Record<string, unknown>): boolean =>
  Object.entries(columns).some(([key, value]) => stored[key] !== (value ?? null));

/**
 * Rows this person may no longer read, inside a record they may still open.
 *
 * The server withholds them from the pull as of 1 Sep 2026, and that is the
 * fence. This is the mirror's half: a pull cannot un-send what an earlier pull
 * already delivered, and nothing else would ever remove those rows —
 * `pruneCatalog` drops whole RECORDS the person lost, never rows inside one
 * they kept.
 *
 * The rule is `rowsKept` in record-grant.ts, the server's own reach
 * (`entryReachFrom` in apps/server/src/access.ts) replayed over the mirror:
 *
 *   Off, or no `view_all_entries`  they hold the record's totals and none of
 *                                  its rows. Every synced row goes.
 *   Own                            they hold only what they wrote.
 *   Their team                     they hold what anyone in their team wrote,
 *                                  themselves included (web-redesign-04
 *                                  Decided 4). Somebody who left the team
 *                                  takes their rows with them.
 *   Everyone, or unknown           nothing goes.
 *
 * A summary without `reach` (an older server) reads its old words, as this
 * always did, and Their team arrives there as `all`: nothing goes.
 *
 * Runs on EVERY pull, not once, which is the reason it is here and not in
 * migration 8. A migration reaches the devices that leaked before the server
 * was fixed; this also reaches the device whose grant is narrowed next month,
 * and it costs an owner nothing — a null grant keeps every row and no
 * statement runs.
 *
 * **PENDING ROWS ARE NEVER TOUCHED.** They are this person's own unsent work,
 * the server has refused none of them, and `push` is the only thing that may
 * drop one. They are also the person's own by definition, so no rule here
 * would have wanted them.
 *
 * A courtesy layer would be wrong to do this. This is not one: the rows are
 * already gone from the server's answers, so removing them makes the device
 * AGREE with the server rather than guess ahead of it.
 *
 * Returns whether any row went, which `upsertCatalog` reports as a change.
 */
const pruneUnreadableRows = (tx: DeviceTx, records: readonly RecordListItem[]): boolean => {
  // Whoever this device is signed in as, compared against entry_mirror.created_by,
  // which the pull stamps from the server. Without one, `rowsKept` keeps every
  // row: deleting on a guess would take the person's own. The next pull after
  // /v1/me lands does it.
  const me = cachedMe<{ user?: { id?: string } }>()?.user?.id ?? null;
  let removed = false;

  for (const record of records) {
    // `?? null` and not a bare null check: the type says `grant` is always
    // there, and the WIRE does not. A server older than this build sends a
    // payload with no such key, and rule 1 of record-grant.ts decides what an
    // unknown grant means — allowed. Nothing is deleted on a payload that never
    // said anything.
    const kept = rowsKept(record.grant ?? null, me);
    if (kept === 'all') continue;
    if (forgetRows(tx, record.id, kept === 'none' ? null : kept)) removed = true;
  }
  return removed;
};

/**
 * Delete one record's synced rows, either all of them or everybody else's.
 *
 * `keepAuthors` null means all go; a list keeps the rows those people wrote.
 * Search documents go with the rows — SEAM 7 of 8; a hit that opens nothing is
 * worse than no hit. Returns whether any went.
 */
const forgetRows = (tx: DeviceTx, recordId: string, keepAuthors: readonly string[] | null): boolean => {
  const mine = eq(entryMirror.pending, false);
  const where =
    keepAuthors === null
      ? and(eq(entryMirror.recordId, recordId), mine)
      : and(
          eq(entryMirror.recordId, recordId),
          mine,
          // `is null or not in` and not `not in` alone: a row with no author is
          // one this device cannot attribute, so it is not provably anybody's
          // in the list and it goes.
          sql`(${entryMirror.createdBy} is null or ${entryMirror.createdBy} not in (${sql.join(
            keepAuthors.map((author) => sql`${author}`),
            sql`, `,
          )}))`,
        );

  const doomed = tx.select({ entryId: entryMirror.entryId }).from(entryMirror).where(where).all();
  if (doomed.length === 0) return false;
  tx.delete(entryMirror).where(where).run();
  for (const row of doomed) forgetDoc(row.entryId);
  return true;
};

/**
 * What one page's catalog says about rows the pull can no longer bring,
 * compared with the catalog rows stored before the page is written (so it
 * runs BEFORE `upsertCatalog`). `rereadReason` in record-grant.ts holds the
 * rule:
 *
 *   widened    records whose rows the person now reads more of
 *   masked     records whose hidden fields moved
 *   firstSeen  records this device holds no row for yet, with entries in
 *              them: given to the person since the last pull, or new. The
 *              engine tells the two apart at the end of the run.
 */
export type CatalogShift = {
  readonly widened: readonly string[];
  readonly masked: readonly string[];
  readonly firstSeen: readonly string[];
};

export const catalogShift = (records: readonly RecordListItem[]): CatalogShift => {
  if (records.length === 0) return { widened: [], masked: [], firstSeen: [] };
  const me = cachedMe<{ user?: { id?: string } }>()?.user?.id ?? null;
  const stored = new Map(
    getDb()
      .select({
        id: recordCatalog.id,
        grantCapabilities: recordCatalog.grantCapabilities,
        grantRowScope: recordCatalog.grantRowScope,
        grantWriteMode: recordCatalog.grantWriteMode,
        grantBalanceVisible: recordCatalog.grantBalanceVisible,
        grantDirection: recordCatalog.grantDirection,
        grantWritableFields: recordCatalog.grantWritableFields,
        grantReach: recordCatalog.grantReach,
        grantTeam: recordCatalog.grantTeam,
        grantMask: recordCatalog.grantMask,
      })
      .from(recordCatalog)
      .where(
        inArray(
          recordCatalog.id,
          records.map((one) => one.id),
        ),
      )
      .all()
      .map((row) => [row.id, row] as const),
  );
  const widened: string[] = [];
  const masked: string[] = [];
  const firstSeen: string[] = [];
  for (const record of records) {
    const before = stored.get(record.id);
    if (before === undefined) {
      if (record.entryCount > 0 && rowsKept(record.grant ?? null, me) !== 'none') firstSeen.push(record.id);
      continue;
    }
    const reason = rereadReason(before, record.grant ?? null, me);
    if (reason === 'widened') widened.push(record.id);
    if (reason === 'masked') masked.push(record.id);
  }
  return { widened, masked, firstSeen };
};

/**
 * Forget what this device holds of records whose hidden fields moved: their
 * synced rows (and search documents), their day buckets, and the version of
 * their cached schema, so the next pull sends the schema again cut to the new
 * mask. The schema itself stays until then, so the record still opens; no row
 * is left in it to show a hidden value. Pending rows stay: they are the
 * person's own unsent work.
 */
const forgetForReread = (tx: DeviceTx, recordIds: readonly string[]): void => {
  if (recordIds.length === 0) return;
  for (const recordId of recordIds) forgetRows(tx, recordId, null);
  tx.delete(aggregateBucket).where(inArray(aggregateBucket.recordId, [...recordIds])).run();
  // A title kept for a pick was cut to the old mask: the next pull names it again.
  tx.delete(pickTitle).where(inArray(pickTitle.recordId, [...recordIds])).run();
  tx.update(schemaCache)
    .set({ fieldSetVer: FORGOTTEN_SCHEMA_VERSION })
    .where(inArray(schemaCache.recordId, [...recordIds]))
    .run();
};

const rereadKey = (tenantId: string): string => `reread:${tenantId}`;

const rereadOf = (tx: DeviceTx, tenantId: string): RereadState =>
  readRereadState(
    tx.select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, rereadKey(tenantId))).get()?.value ??
      null,
  );

const storeReread = (tx: DeviceTx, tenantId: string, state: RereadState): void => {
  if (!state.restart && state.seen.length === 0) {
    tx.delete(syncMeta).where(eq(syncMeta.key, rereadKey(tenantId))).run();
    return;
  }
  const value = writeRereadState(state);
  tx.insert(syncMeta)
    .values({ key: rereadKey(tenantId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/**
 * Store what one page's catalog shift owes (`noteShift` in reread.ts), and
 * forget the masked records' rows at once, so a field set to None stops
 * showing now. Called BEFORE that page's `upsertCatalog`: once the new catalog
 * is written, no later run can find the shift again, so a run that fails or is
 * killed after it must already have stored it. In one transaction, so the
 * rows are not forgotten without the restart that brings them back.
 */
export const noteReread = (tenantId: string, shift: CatalogShift, fill: boolean): void => {
  if (shift.widened.length === 0 && shift.masked.length === 0 && (fill || shift.firstSeen.length === 0)) return;
  getDb().transaction((tx) => {
    forgetForReread(tx, shift.masked);
    storeReread(tx, tenantId, noteShift(rereadOf(tx, tenantId), shift, fill));
  });
};

/**
 * Count the first-seen records this business owes (`givenSince`), on the last
 * page, after its rows and before the prune. A fill counts nothing: it brought
 * every row there is.
 */
export const settleReread = (tenantId: string, fill: boolean): void => {
  const state = getDb().transaction((tx) => rereadOf(tx, tenantId));
  if (state.seen.length === 0) return;
  const given = fill ? [] : givenSince(state.seen);
  getDb().transaction((tx) => storeReread(tx, tenantId, settleSeen(rereadOf(tx, tenantId), given)));
};

/**
 * When a re-read is owed, start this business's pull again from the
 * beginning and clear the debt, together: the next run is a fill, as on a new
 * phone, and brings every row the person may now read. The mirror is kept;
 * rows already held are written over by the same versions. Called only after a run finishes.
 * True when the pull was restarted, so the engine runs once more.
 */
export const restartIfReread = (tenantId: string): boolean =>
  getDb().transaction((tx) => {
    const state = rereadOf(tx, tenantId);
    if (!state.restart) return false;
    tx.delete(syncMeta).where(eq(syncMeta.key, sinceKey(tenantId))).run();
    storeReread(tx, tenantId, { restart: false, seen: state.seen });
    return true;
  });

/**
 * Which of these first-seen records were given to the person since the last
 * pull, rather than made since it: `givenRecords` in reread.ts holds the rule.
 * Counted from what the device holds now, so it is asked after the last page's
 * rows are in and before the prune, which takes rows outside the window.
 *
 * A row counts once the server has acked it (`version > 0`): a pending create
 * is the person's own work and says nothing about what the pull brought.
 */
export const givenSince = (recordIds: readonly string[]): string[] => {
  if (recordIds.length === 0) return [];
  const me = cachedMe<{ user?: { id?: string } }>()?.user?.id ?? null;
  const db = getDb();
  const catalog = db
    .select({
      id: recordCatalog.id,
      entryCount: recordCatalog.entryCount,
      grantCapabilities: recordCatalog.grantCapabilities,
      grantRowScope: recordCatalog.grantRowScope,
      grantWriteMode: recordCatalog.grantWriteMode,
      grantBalanceVisible: recordCatalog.grantBalanceVisible,
      grantDirection: recordCatalog.grantDirection,
      grantWritableFields: recordCatalog.grantWritableFields,
      grantReach: recordCatalog.grantReach,
      grantTeam: recordCatalog.grantTeam,
      grantMask: recordCatalog.grantMask,
    })
    .from(recordCatalog)
    .where(inArray(recordCatalog.id, [...recordIds]))
    .all();
  const held = new Map(
    db
      .select({ recordId: entryMirror.recordId, held: sql<number>`count(*)` })
      .from(entryMirror)
      .where(
        and(
          inArray(entryMirror.recordId, [...recordIds]),
          eq(entryMirror.isCurrent, true),
          isNull(entryMirror.deletedAt),
          sql`${entryMirror.version} > 0`,
        ),
      )
      .groupBy(entryMirror.recordId)
      .all()
      .map((row) => [row.recordId, Number(row.held)] as const),
  );
  // A record pruned from the catalog since it was seen is not here, and is
  // not read again: the person can no longer open it.
  return givenRecords(
    catalog.map((row) => ({
      id: row.id,
      entryCount: row.entryCount,
      held: held.get(row.id) ?? 0,
      kept: rowsKept(unpackGrant(row), me),
    })),
  );
};

/**
 * Drop everything local about records the caller may no longer read: catalog
 * row, mirrored entries, cached schema, and the insight view. The outbox is
 * left alone on purpose — a pending op for a lost record is refused on the
 * next push and dropped then, with its reason.
 *
 * Returns the pruned ids so the engine can bump the catalog.
 *
 * **THE DANGEROUS ONE.** `readableRecordIds` is one business's answer to "what
 * may this person read", so every record of every OTHER business on the phone
 * is missing from it. Without the tenant predicate on the candidate select, a
 * pull for one shop deletes the other shop's records, entries, schemas and
 * search documents — and the cursor for that shop has already moved past those
 * entries, so they do not come back on the next pull. That is unrecoverable
 * without a reinstall.
 *
 * So when the business cannot be named and this phone holds more than one, this
 * prunes NOTHING. A stale record left on a device costs a row on a home screen
 * until the next pull that names its business; the alternative costs the data.
 */
export const pruneCatalog = (readableRecordIds: string[], tenantId?: string | null): string[] => {
  const db = getDb();
  const { tenant, ambiguous } = syncScope(tenantId);
  if (ambiguous) return [];
  const keep = new Set(readableRecordIds);
  const pruned = db
    .select({ id: recordCatalog.id })
    .from(recordCatalog)
    .where(sameTenant(recordCatalog.tenantId, tenant))
    .all()
    .map((row) => row.id)
    .filter((id) => !keep.has(id));
  if (pruned.length === 0) return [];
  // The lines records under each lost record, at both levels: they are never
  // in the catalog, so the reach that took the record takes its lines too
  // (Decision 2: a line is read by whoever reads its root entry, and only them).
  const prunedLines = pruned.flatMap((recordId) => {
    const schema = schemaFor(recordId);
    return schema === null ? [] : linesRecordsUnder(schema);
  });
  const gone = [...pruned, ...prunedLines];

  db.transaction((tx) => {
    tx.delete(entryMirror).where(inArray(entryMirror.recordId, gone)).run();
    tx.delete(pickTitle).where(inArray(pickTitle.recordId, gone)).run();
    tx.delete(schemaCache).where(inArray(schemaCache.recordId, gone)).run();
    tx.delete(insightLayout).where(inArray(insightLayout.recordId, pruned)).run();
    // The nudge dismissal goes with the record it belonged to. A record this
    // device may no longer read cannot come back, and a key that outlived it
    // would sit in sync_meta for as long as the install does.
    tx.delete(syncMeta)
      .where(inArray(syncMeta.key, pruned.map(nudgeKey)))
      .run();
    tx.delete(recordCatalog).where(inArray(recordCatalog.id, pruned)).run();
    // SEAM 7 of 8. A record the caller may no longer read must not be
    // searchable.
    forgetRecordDocs(pruned);
  });
  for (const recordId of pruned) markSearchStale(recordId);
  for (const recordId of pruned) {
    dropView(recordId, execRaw);
    dropFieldIndexes(recordId);
  }
  return pruned;
};

/* ------------------------------------------------------------- insights */

/**
 * Which insight cards sit on one record, or null when nobody has said.
 *
 * Null and `[]` are different answers. Null means this record has never been
 * customized, so the caller falls back to `defaultLayoutFor(schema)` and shows
 * what the template asked for. `[]` means the user took every card off, and
 * that choice must survive a relaunch. Unreadable JSON reads as null, which
 * lands on the template default rather than on a blank strip.
 */
export const getInsightLayout = (recordId: string): InsightLayoutEntry[] | null => {
  const row = getDb()
    .select({ layoutJson: insightLayout.layoutJson })
    .from(insightLayout)
    .where(eq(insightLayout.recordId, recordId))
    .get();
  return row === undefined ? null : parseLayout(row.layoutJson);
};

/** The whole list, in one write. Order is the array order. */
export const saveInsightLayout = (
  recordId: string,
  layout: readonly InsightLayoutEntry[],
): void => {
  const now = new Date().toISOString();
  const layoutJson = JSON.stringify(layout);
  getDb()
    .insert(insightLayout)
    .values({ recordId, layoutJson, updatedAt: now })
    .onConflictDoUpdate({
      target: insightLayout.recordId,
      set: { layoutJson, updatedAt: now },
    })
    .run();
};

/**
 * Forget this record's arrangement, so it draws its template's defaults again.
 *
 * **Delete the row; never write `[]`.** The two mean different things (see
 * `getInsightLayout`): `[]` is "the person took every insight off" and must
 * survive a relaunch, where a missing row is "nobody has arranged this". Edit
 * mode's "Reset to the usual insights" is the second, and so is Undo of the
 * first change on a record that had never been arranged — writing the defaults
 * down would make Reset appear for a record that is already usual.
 */
export const clearInsightLayout = (recordId: string): void => {
  getDb().delete(insightLayout).where(eq(insightLayout.recordId, recordId)).run();
};

/**
 * Run one insight against the record's view and shape the rows.
 *
 * Null means the number cannot be had right now — almost always because the
 * record's schema has not been pulled, so `v_<record>` does not exist yet. A
 * card draws a quiet placeholder for that rather than an error: the view
 * appears the moment `cacheSchemas` stores the schema.
 *
 * @param today the device's day. Passed in so a caller can compute a whole
 *              screen of cards against one instant, and so the boundary is
 *              visible rather than hidden inside a clock read.
 */
/**
 * The schemas a card may need, read straight out of the cache.
 *
 * A hop has to know which record a reference points at and what the field on
 * the other side holds, and only the cached schema knows either. Synchronous,
 * because a card is compiled during a render pass; a record this device has not
 * pulled answers null, and the compiler then draws nothing rather than a number
 * it cannot stand behind.
 *
 * One object per call rather than one module-level constant, so a schema cached
 * a moment ago is seen by the next card without anything to invalidate.
 */
const schemaContext = (): InsightCompileContext => ({ schemaOf: schemaFor });

/** One compiled record insight, and how to turn its rows into a result. */
/**
 * The oldest day this record still holds, or null when it holds everything.
 *
 * One column, read at compile time rather than cached, because the prune writes
 * it at the end of a pull and a card drawn a moment later must see it.
 */
export const floorDayOf = (recordId: string): string | null =>
  getDb()
    .select({ floorDay: recordCatalog.floorDay })
    .from(recordCatalog)
    .where(eq(recordCatalog.id, recordId))
    .get()?.floorDay ?? null;

/**
 * The oldest day a card can trust its rows from: its record's floor, or for a
 * card over totals of several records the latest floor among them. A record
 * whose older rows went makes every total over it short, so one such record
 * sends the whole card below its floor to the buckets, which refuse it.
 */
const cardFloorDayOf = (recordId: string, spec: InsightSpec): string | null =>
  spec.measure.agg !== 'formula'
    ? floorDayOf(recordId)
    : [...new Set([recordId, ...aggLeavesOf(spec.measure.expr).map((leaf) => leaf.recordId ?? recordId)])]
        .map(floorDayOf)
        .reduce<string | null>(laterFloor, null);

/** `cardFloorDayOf`, for the Dashboard's question of whether a card must ask the server. */
export const cardFloorDay = (recordId: string, spec: InsightSpec): string | null => cardFloorDayOf(recordId, spec);

/**
 * What a sync last changed in the records a card reads, as one string: per
 * record its row count, its headline, the newest row the mirror holds and the
 * newest bucket. A kept server answer is asked again the moment this moves,
 * so a sync that touched Jobs or Received refreshes Pending at once.
 */
export const cardReadsSignature = (recordId: string, spec: InsightSpec): string =>
  cardRecordIds(recordId, spec)
    .map((id) => {
      const row = queryRaw<{ rows: number | null; headline: number | null; seq: number | null; bucket: number | null }>(
        `SELECT (SELECT entry_count FROM record_catalog WHERE id = ?) AS rows,
                (SELECT headline_value FROM record_catalog WHERE id = ?) AS headline,
                (SELECT MAX(server_seq) FROM entry_mirror WHERE record_id = ?) AS seq,
                (SELECT MAX(as_of_seq) FROM aggregate_bucket WHERE record_id = ?) AS bucket`,
        [id, id, id, id],
      )[0];
      return `${id}:${row?.rows ?? ''}:${row?.headline ?? ''}:${row?.seq ?? ''}:${row?.bucket ?? ''}`;
    })
    .join('|');

/** A server answer kept for a Dashboard card (`insight-answers.ts`), or null. In `sync_meta`, keyed by tenant, person and card. */
export const keptInsightAnswer = (key: string): string | null =>
  getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, key)).get()?.value ?? null;

/** Keeps a server answer for a Dashboard card, over the one before it. */
export const keepInsightAnswer = (key: string, value: string): void => {
  getDb().insert(syncMeta).values({ key, value }).onConflictDoUpdate({ target: syncMeta.key, set: { value } }).run();
};

/** Drops a Dashboard card's kept answer: the server refused the card. */
export const forgetInsightAnswer = (key: string): void => {
  getDb().delete(syncMeta).where(eq(syncMeta.key, key)).run();
};

const prepareInsight = (
  recordId: string,
  spec: InsightSpec,
  today: string,
  cut: RuntimeCut | undefined,
  /**
   * Whether the pruned mirror is taken into account.
   *
   * True for a card, which makes a claim about a period and must not make one
   * the rows can no longer support. False for the filter sheet's own questions
   * — which values are in use, what the largest amount is — because those ask
   * what this device HOLDS, and the filter they size runs over the same rows.
   */
  horizon: boolean,
): PreparedInsight => {
  const compile = (one: InsightSpec) =>
    horizon
      ? compileInsightAtHorizon(one, recordId, today, { floorDay: cardFloorDayOf(recordId, one) }, cut, schemaContext())
      : compileInsight(one, recordId, today, cut, schemaContext());
  const { sql: statement, params } = compile(spec);
  // Grouped by a multi select, the slices overlap, so the headline is a
  // second statement that counts each entry once (Decision 8).
  const total =
    spec.shape === 'breakdown' && groupsOverlap(spec, recordId, schemaContext())
      ? compile(overlapTotalSpec(spec))
      : null;
  const shape = (rows: never[], totalRows: never[] = []): InsightResult => {
    switch (spec.shape) {
      case 'scalar':
        return readScalar(rows as ScalarRow[]);
      case 'scalar_pair':
        return readPair(rows as PairRow[]);
      case 'series':
        // A cut replaced the card's own span of days, so the gaps have to be
        // filled over the same window or the line would be drawn against the
        // wrong dates.
        return cut?.window == null
          ? fillSeries(rows as GroupedRow[], spec.days, today)
          : fillSeriesRange(rows as GroupedRow[], cut.window);
      case 'breakdown': {
        const ranked = cutBreakdown(rows as GroupedRow[], spec.limit);
        return total === null ? ranked : { ...ranked, overlap: { total: readScalar(totalRows as ScalarRow[]).value } };
      }
    }
  };
  return {
    sql: statement,
    params: [...params],
    total: total === null ? null : { sql: total.sql, params: [...total.params] },
    shape,
  };
};

type PreparedInsight = {
  readonly sql: string;
  readonly params: (string | number)[];
  /** A second statement, for a breakdown whose groups overlap: its headline. */
  readonly total: { readonly sql: string; readonly params: (string | number)[] } | null;
  readonly shape: (rows: never[], totalRows?: never[]) => InsightResult;
};

export const computeInsight = (
  recordId: string,
  spec: InsightSpec,
  today: string = workspaceToday(),
  cut?: RuntimeCut,
  options: { readonly horizon?: boolean } = {},
): InsightResult | null => {
  try {
    const prepared = prepareInsight(recordId, spec, today, cut, options.horizon ?? true);
    const totalRows = prepared.total === null ? [] : queryRaw<never>(prepared.total.sql, prepared.total.params);
    return prepared.shape(queryRaw<never>(prepared.sql, prepared.params), totalRows);
  } catch {
    // A missing view is the expected failure and is not worth a crash. So is a
    // pair card handed a window, which the caller is meant to have hidden
    // first — this catch turns that slip into a quiet empty card. Any other
    // fault is a defect in the compiler, which its tests cover.
    return null;
  }
};

/**
 * `computeInsight`, read on the second connection, off the JS thread.
 *
 * The same statement and the same shaping, so the two cannot give different
 * numbers. See `queryRawAsync` for why.
 */
export const computeInsightAsync = async (
  recordId: string,
  spec: InsightSpec,
  today: string = workspaceToday(),
  cut?: RuntimeCut,
  options: { readonly horizon?: boolean } = {},
): Promise<InsightResult | null> => {
  try {
    const prepared = prepareInsight(recordId, spec, today, cut, options.horizon ?? true);
    const totalRows =
      prepared.total === null ? [] : await queryRawAsync<never>(prepared.total.sql, prepared.total.params);
    return prepared.shape(await queryRawAsync<never>(prepared.sql, prepared.params), totalRows);
  } catch {
    return null;
  }
};

/**
 * The values actually recorded in one field of this record, most used first.
 *
 * This is what the condition builder offers. It deliberately does NOT list the
 * whole directory behind a reference field: a shop's Items may hold five
 * hundred rows, of which eleven have ever been sold, and a picker of five
 * hundred rows to choose one of eleven is not a picker. Ranking by how often a
 * value was actually used puts the answer near the top.
 *
 * It is also the only offline-correct source. The entry form's reference picker
 * asks the server, which is fine there — creating a new item needs the server
 * anyway. An insight is computed from the mirror, so its conditions must be
 * built from the mirror too, or a condition could name a value this device
 * cannot count.
 *
 * No new SQL: this is a breakdown that counts rows, which the compiler already
 * builds and its tests already cover.
 */
export const usedValuesFor = (
  recordId: string,
  fieldId: string,
): { readonly key: string; readonly count: number }[] => {
  // `horizon: false` on purpose. This asks which values are IN THE MIRROR, so
  // it can offer them as filters over the same rows. It makes no claim about
  // history, and a pruned record must still be filterable by what it holds.
  const result = computeInsight(
    recordId,
    {
      shape: 'breakdown',
      measure: { agg: 'rows' },
      groupFieldId: fieldId,
      scope: 'all',
      limit: GROUP_SCAN_LIMIT,
    },
    undefined,
    undefined,
    { horizon: false },
  );
  if (result === null || result.shape !== 'breakdown') return [];
  return result.slices.map((slice) => ({ key: slice.key, count: slice.value }));
};

/**
 * The largest figure this record holds in one field, by size.
 *
 * What sizes the steps of a range control. By SIZE and not by value, because a
 * money-out record materializes every amount negative and its steps must still
 * be the steps of that shop's real amounts — the same reason a breakdown ranks
 * by magnitude.
 *
 * No new SQL: two scalars the compiler already builds, and null when the record
 * has recorded nothing in that field.
 */
export const largestValueOf = (recordId: string, fieldId: string): number | null => {
  const read = (agg: 'max' | 'min'): number | null => {
    // `horizon: false`, for the reason `usedValuesFor` gives: this sizes a
    // control over the rows this device holds, and the filter it sizes runs
    // over those same rows.
    const result = computeInsight(
      recordId,
      { shape: 'scalar', measure: { agg, of: { kind: 'field', fieldId } }, scope: 'all' },
      undefined,
      undefined,
      { horizon: false },
    );
    return result === null || result.shape !== 'scalar' ? null : result.value;
  };
  const high = read('max');
  const low = read('min');
  if (high === null && low === null) return null;
  return Math.abs(high ?? 0) >= Math.abs(low ?? 0) ? (high ?? low) : (low ?? high);
};

/* --------------------------------------------------- business insights */

/**
 * The business's own insight strip, or null when nobody has customized it.
 *
 * Null and `[]` are different answers, as they are for a record: null means
 * "derive the strip from the records this business keeps", `[]` means "somebody
 * took every card off".
 */
export const getBusinessInsightLayout = (
  tenantId: string,
): BusinessInsightLayoutEntry[] | null => {
  const row = getDb()
    .select({ layoutJson: businessInsightLayout.layoutJson })
    .from(businessInsightLayout)
    .where(eq(businessInsightLayout.tenantId, tenantId))
    .get();
  return row === undefined ? null : parseBusinessLayout(row.layoutJson);
};

/**
 * Forget this business's arrangement: its records answer again.
 *
 * Deletes the row rather than writing `[]`, for the reason `clearInsightLayout`
 * gives for a record — `[]` means somebody took every card off, and the
 * Dashboard would show nothing where it should show what its records imply.
 */
export const clearBusinessInsightLayout = (tenantId: string): void => {
  getDb().delete(businessInsightLayout).where(eq(businessInsightLayout.tenantId, tenantId)).run();
};

/** The whole strip, in one write. Order is the array order. */
export const saveBusinessInsightLayout = (
  tenantId: string,
  layout: readonly BusinessInsightLayoutEntry[],
): void => {
  const now = new Date().toISOString();
  const layoutJson = JSON.stringify(layout);
  getDb()
    .insert(businessInsightLayout)
    .values({ tenantId, layoutJson, updatedAt: now })
    .onConflictDoUpdate({
      target: businessInsightLayout.tenantId,
      set: { layoutJson, updatedAt: now },
    })
    .run();
};

/** Where the Dashboard cards one business's setup drafted are kept on this device. */
const setupCardsKey = (tenantId: string): string => `setup_cards:${tenantId}`;

/**
 * The Dashboard cards this business's setup drafted across records, as the
 * last pull served them (`SyncPullResponse.boardCards`). Empty when it drafted
 * none, or before the first pull. A device with no Dashboard of its own
 * starts from them (`startingBusinessLayoutFor`).
 */
export const getSetupBoardCards = (tenantId: string): WorkspaceBoardCard[] => {
  const row = getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, setupCardsKey(tenantId))).get();
  if (row === undefined) return [];
  try {
    const held: unknown = JSON.parse(row.value);
    return Array.isArray(held) ? (held as WorkspaceBoardCard[]) : [];
  } catch {
    return [];
  }
};

/**
 * Keep the cards a pull served, all of them in one write; none deletes the
 * row. True when they differ from what was kept, so the Dashboard is told.
 */
export const saveSetupBoardCards = (tenantId: string, cards: readonly WorkspaceBoardCard[]): boolean => {
  const key = setupCardsKey(tenantId);
  const held = getDb().select({ value: syncMeta.value }).from(syncMeta).where(eq(syncMeta.key, key)).get();
  if (cards.length === 0) {
    if (held === undefined) return false;
    getDb().delete(syncMeta).where(eq(syncMeta.key, key)).run();
    return true;
  }
  const value = JSON.stringify(cards);
  if (held?.value === value) return false;
  getDb().insert(syncMeta).values({ key, value }).onConflictDoUpdate({ target: syncMeta.key, set: { value } }).run();
  return true;
};

/**
 * Run one business-wide insight and shape the rows.
 *
 * Null means the number cannot be had. Two causes, both temporary and both
 * drawn as the same quiet placeholder: a money card on a business with no
 * currency yet, which the compiler refuses outright, and a SQL fault, which
 * would be a defect in the compiler that its own tests cover.
 */
/**
 * The latest day any record in this business has been pruned to, or null.
 *
 * The LATEST, because a business-wide statement sums every record at once: one
 * record missing its older rows makes the whole sum short, and nothing in the
 * answer says so. So the business is only as complete as its least complete
 * record.
 */
export const businessFloorDay = (tenantId: string): string | null =>
  queryRaw<{ floor: string | null }>(
    'SELECT MAX(floor_day) AS floor FROM record_catalog WHERE tenant_id = ?',
    [tenantId],
  )[0]?.floor ?? null;

export const computeBusinessInsight = (
  spec: BusinessInsightSpec,
  context: BusinessContext,
): InsightResult | null => {
  // A card hosted on the Dashboard is a record's own question, so it is run by
  // the record compiler over the host's view. One compiler per grammar: a
  // second one here would be a second set of numbers to keep in step.
  if (isHostedSpec(spec)) return computeInsight(spec.host, spec.record, context.today);
  // A pinned money figure is the server's to answer (the phone holds no ledger lines): nothing is run here.
  if (isLedgerTileSpec(spec)) return null;
  const prepared = prepareBusinessInsight(spec, context);
  if (prepared === null) return null;
  try {
    return prepared.shape(queryRaw<never>(prepared.sql, prepared.params));
  } catch {
    return null;
  }
};

/**
 * `computeBusinessInsight`, read on the second connection, off the JS thread.
 *
 * What the Dashboard runs. The same statement and the same shaping as the
 * synchronous one; see `queryRawAsync` for why it exists.
 */
export const computeBusinessInsightAsync = async (
  spec: BusinessInsightSpec,
  context: BusinessContext,
): Promise<InsightResult | null> => {
  if (isHostedSpec(spec)) return computeInsightAsync(spec.host, spec.record, context.today);
  if (isLedgerTileSpec(spec)) return null;
  const prepared = prepareBusinessInsight(spec, context);
  if (prepared === null) return null;
  try {
    return prepared.shape(await queryRawAsync<never>(prepared.sql, prepared.params));
  } catch {
    return null;
  }
};

/** One compiled business insight and its shaping, or null when it cannot be asked. */
const prepareBusinessInsight = (
  spec: BusinessOwnSpec,
  context: BusinessContext,
): { readonly sql: string; readonly params: (string | number)[]; readonly shape: (rows: never[]) => InsightResult } | null => {
  const compiled = compileBusinessInsightAtHorizon(spec, context, { floorDay: businessFloorDay(context.tenantId) });
  if (compiled === null) return null;
  const shape = (rows: never[]): InsightResult => {
    switch (spec.shape) {
      case 'scalar':
        return readScalar(rows as ScalarRow[]);
      case 'scalar_pair':
        return readPair(rows as PairRow[]);
      case 'series':
        return fillSeries(rows as GroupedRow[], spec.days, context.today);
      case 'breakdown':
        return cutBreakdown(rows as GroupedRow[], spec.limit);
    }
  };
  return { sql: compiled.sql, params: [...compiled.params], shape };
};

/**
 * How many different people have written an entry on this device.
 *
 * The gallery asks before offering a card that breaks work down by writer: on a
 * one-person shop such a card draws a single row saying "You, 81", which
 * answers a question nobody has. This device's own unsent rows are folded onto
 * the signed-in person first, so an offline sale does not look like a second
 * writer.
 */
export const writerCount = (meId: string | null, tenantId?: string | null): number => {
  // Asked record by record: a business has a handful of records, and each ask
  // is three seeks on `entry_mirror_record_live`. One business-wide index would
  // cost forty megabytes on a business of five lakh rows to save a few seeks.
  const tenant = shownTenant(tenantId);
  const records = getDb()
    .select({ id: recordCatalog.id })
    .from(recordCatalog)
    .where(tenant === null ? undefined : eq(recordCatalog.tenantId, tenant))
    .all();
  const writers = new Set<string>();
  for (const record of records) {
    for (const writer of writersOf('record_id = ?', [record.id], meId)) writers.add(writer);
    if (writers.size >= 2) break;
  }
  return writers.size;
};

/**
 * Who wrote the rows under `scope`, exact up to two people.
 *
 * Every caller asks one question, "is there a second writer?", so the answer
 * stops at the third distinct raw value instead of counting them all: three,
 * because this device's own unsent rows say `local` and fold onto the signed-in
 * person, which can turn two raw values into one writer. Each step is one seek
 * on `entry_mirror_record_live` (migration 11) for the next writer after the last,
 * so the cost does not grow with the rows.
 */
const writersOf = (scope: string, params: readonly string[], meId: string | null): Set<string> => {
  const fold = meId ?? 'local';
  const seen = new Set<string>();
  let after: string | null = null;
  for (let step = 0; step < 3; step += 1) {
    const row: { writer: string | null } | null = getDb().$client.getFirstSync<{ writer: string | null }>(
      `SELECT MIN(created_by) AS writer
         FROM entry_mirror
        WHERE ${scope}
          AND is_current = 1
          AND deleted_at IS NULL
          AND created_by IS NOT NULL${after === null ? '' : ' AND created_by > ?'}`,
      after === null ? [...params] : [...params, after],
    );
    const writer: string | null = row?.writer ?? null;
    if (writer === null) break;
    seen.add(writer === 'local' ? fold : writer);
    after = writer;
  }
  return seen;
};

/**
 * How many different people have written an entry in ONE record.
 *
 * The entry list asks before drawing its author line: a record one person keeps
 * would say "by you" on every row, which answers a question nobody has. The
 * line appears the moment a second person's entry lands in this record, and
 * this device's own unsent rows fold onto the signed-in person first, so an
 * offline sale does not turn a solo record into a shared one.
 *
 * Keyed by record id, so no tenant predicate: a record id is a uuid, unique
 * across businesses, the same reasoning the keyed tables in db-schema.ts state.
 */
export const recordWriterCount = (meId: string | null, recordId: string): number =>
  writersOf('record_id = ?', [recordId], meId).size;

/**
 * How many records this device holds for each business it holds any for.
 *
 * What the switcher sheet says under each name. A business with no row here has
 * never been synced on this phone, which the sheet says outright rather than
 * drawing a zero — "0 records" and "not on this phone yet" are different facts,
 * and only one of them is true.
 */
export const catalogCounts = (): Record<string, number> => {
  const rows = getDb()
    .select({ tenantId: recordCatalog.tenantId, n: sql<number>`count(*)` })
    .from(recordCatalog)
    .groupBy(recordCatalog.tenantId)
    .all();
  const counts: Record<string, number> = {};
  for (const row of rows) {
    if (row.tenantId !== null) counts[row.tenantId] = row.n;
  }
  return counts;
};

/* ------------------------------------------------------------- identity */

/** The one sync_meta key holding the last /v1/me answer. */
const ME_KEY = 'me';

/**
 * Remember who is signed in and which businesses they belong to.
 *
 * Without this the app cannot start offline. The mirror can hold a year of
 * entries, but every screen keys off the tenant id from `/v1/me`, so one
 * unreachable request used to blank a device that had all the data it needed.
 *
 * The server stays the authority: this is only consulted when the fetch fails,
 * every `/v1` call still enforces its own grants, and the next reachable
 * request overwrites it. A membership revoked while offline therefore survives
 * until the device next reaches the server, and can read nothing in the
 * meantime beyond what already synced to it.
 */
export const cacheMe = (me: unknown): void => {
  const value = JSON.stringify(me);
  getDb()
    .insert(syncMeta)
    .values({ key: ME_KEY, value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/** The last /v1/me answer, or null when this device has never had one. */
export const cachedMe = <T>(): T | null => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, ME_KEY))
    .get();
  if (row === undefined) return null;
  try {
    return JSON.parse(row.value) as T;
  } catch {
    return null;
  }
};

/** sync_meta key: one business's writer names, as a JSON id-to-name record. */
const writersKeyOf = (tenantId: string): string => `writers:${tenantId}`;

/**
 * Remember what the member list last called each writer.
 *
 * The entry list's author line reads THIS, never the network: the list is
 * drawn from the mirror offline, so the names beside it must answer offline
 * too. `use-writer-names.ts` refreshes it once per run; between runs the last
 * known name stands, which is the same bargain `cacheMe` states above. A name
 * this cache does not hold is drawn as "by someone", not as a uuid.
 */
export const cacheWriterNames = (tenantId: string, names: Record<string, string>): void => {
  const value = JSON.stringify(names);
  getDb()
    .insert(syncMeta)
    .values({ key: writersKeyOf(tenantId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};

/** The last cached names for one business. Empty when none have ever landed. */
export const cachedWriterNames = (tenantId: string): Record<string, string> => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, writersKeyOf(tenantId)))
    .get();
  if (row === undefined) return {};
  try {
    return JSON.parse(row.value) as Record<string, string>;
  } catch {
    return {};
  }
};

/**
 * Sign-out wipes the device. Everything local goes: the mirror, the outbox,
 * the catalog, the schemas, both layouts, every cursor, and the per-record
 * views.
 *
 * The outbox is the reason this must be whole. A pending op left behind is
 * pushed by the NEXT account under ITS tenant id. The server refuses it as a
 * 4xx, push reads that as final, and the previous person's unsynced entries
 * are gone. Clearing at sign-out is what keeps one person's writes from ever
 * being sent as another's.
 *
 * Ops now carry the business they belong to, so a leftover op can no longer be
 * pushed as another BUSINESS's — but sign-out is about the ACCOUNT, and the
 * next account on this phone is a different person. So this still wipes
 * everything, tenant by tenant and cursor by cursor, including which business
 * was last chosen, and the setup thread kept for the next time setup opens.
 */
export const forgetDevice = (): void => {
  // Leaving setup keeps what was said (B13); signing out must not hand it to
  // the next person on this phone.
  useSetupDraftStore.getState().forget();
  // Nor the titles a search showed them.
  shownTitles.clear();
  // Nor a Dashboard answer still on its way: it lands after the wipe and keeps nothing.
  forgetAnswerAsks();

  const db = getDb();
  const recordIds = db
    .select({ id: recordCatalog.id })
    .from(recordCatalog)
    .all()
    .map((row) => row.id);

  db.transaction((tx) => {
    tx.delete(entryMirror).run();
    tx.delete(pickTitle).run();
    tx.delete(outbox).run();
    tx.delete(schemaCache).run();
    tx.delete(insightLayout).run();
    tx.delete(businessInsightLayout).run();
    tx.delete(recordCatalog).run();
    tx.delete(syncMeta).run();
    // SEAM 8 of 8. Sign-out leaves nothing, and a search index left behind
    // would let the next person search the last person's records.
    runRaw(clearWordsSql(), []);
    runRaw(clearRowsSql(), []);
  });

  for (const recordId of recordIds) {
    dropView(recordId, execRaw);
    dropFieldIndexes(recordId);
  }
};

/* ------------------------------------------------------- the day buckets */

/**
 * Store what the pull sent: one row per record, per measure, per day.
 *
 * An upsert and not a replace. The pull sends every bucket of every readable
 * record on the page that catches the device up, so a plain insert would
 * conflict and a delete-then-insert would leave the table empty for the width
 * of a transaction — which a card drawn at that moment would read as a zero.
 *
 * `as_of_seq` takes the larger of the two. It is what says a bucket has moved,
 * and it must never go backwards on a page that arrived out of order.
 *
 * Answers the record ids whose buckets changed, so the engine can bump exactly
 * those records and no others.
 */
export const applyBuckets = (buckets: readonly SyncBucketRow[], tenantId: string): string[] => {
  if (buckets.length === 0) return [];
  const touched = new Set<string>();
  getDb().transaction((tx) => {
    for (const bucket of buckets) {
      tx.insert(aggregateBucket)
        .values({
          tenantId,
          recordId: bucket.recordId,
          day: bucket.day,
          measureFieldId: bucket.measureFieldId,
          value: bucket.value,
          rows: bucket.rows,
          moneyIn: bucket.moneyIn,
          moneyOut: bucket.moneyOut,
          asOfSeq: bucket.asOfSeq,
        })
        .onConflictDoUpdate({
          target: [aggregateBucket.tenantId, aggregateBucket.recordId, aggregateBucket.day],
          /**
           * The numbers move only when the answer carrying them is at least as
           * new as the one already stored, and `as_of_seq` never goes backwards.
           *
           * Buckets travel only on the page that catches a run up, so two
           * answers for one day are two runs and the later one wins. Writing
           * the numbers unconditionally would let a slow answer overwrite a
           * newer one while the stamp still claimed the newer horizon, and
           * nothing afterwards could tell.
           */
          set: {
            measureFieldId: sql`CASE WHEN excluded.as_of_seq >= ${aggregateBucket.asOfSeq} THEN excluded.measure_field_id ELSE ${aggregateBucket.measureFieldId} END`,
            value: sql`CASE WHEN excluded.as_of_seq >= ${aggregateBucket.asOfSeq} THEN excluded.value ELSE ${aggregateBucket.value} END`,
            rows: sql`CASE WHEN excluded.as_of_seq >= ${aggregateBucket.asOfSeq} THEN excluded.rows ELSE ${aggregateBucket.rows} END`,
            moneyIn: sql`CASE WHEN excluded.as_of_seq >= ${aggregateBucket.asOfSeq} THEN excluded.money_in ELSE ${aggregateBucket.moneyIn} END`,
            moneyOut: sql`CASE WHEN excluded.as_of_seq >= ${aggregateBucket.asOfSeq} THEN excluded.money_out ELSE ${aggregateBucket.moneyOut} END`,
            asOfSeq: sql`max(${aggregateBucket.asOfSeq}, excluded.as_of_seq)`,
          },
        })
        .run();
      touched.add(bucket.recordId);
    }
  });
  return [...touched];
};

/**
 * Which records this device may prune, and which it must keep whole.
 *
 * Read off the cached schemas, which is the only place the device knows either
 * fact. A record with no time axis has no day the server agrees with; a
 * list some other record points AT is kept whole, because a picker reads all
 * of it. A record a pick targets is pruned like any other: a pick's title
 * falls back to the one the pull sent, or the prune kept (`pickTitle`, step 9.8).
 */
export type PrunableRecord = { readonly recordId: string; readonly timeAxisFieldId: string };

export const prunableRecords = (tenantId: string): PrunableRecord[] => {
  const rows = getDb()
    .select({ id: recordCatalog.id, rowScope: recordCatalog.grantRowScope })
    .from(recordCatalog)
    .where(eq(recordCatalog.tenantId, tenantId))
    .all();
  const axis = new Map<string, string>();
  const schemas: RecordSchemaResponse[] = [];
  for (const row of rows) {
    const schema = schemaFor(row.id);
    if (schema === null) continue;
    schemas.push(schema);
    const binding = schema.bindings.time_axis;
    if (binding !== undefined && 'fieldId' in binding) axis.set(row.id, binding.fieldId);
  }
  // A list something points at is kept whole, as the server keeps it; a record a pick targets is windowed (step 9.8).
  const kinds = new Map(getDb().select({ id: recordCatalog.id, kind: recordCatalog.kind }).from(recordCatalog).where(eq(recordCatalog.tenantId, tenantId)).all().map((row) => [row.id, row.kind] as const));
  const referenced = pointedRecords(schemas, (recordId) => kinds.get(recordId) === 'record');
  return rows
    .filter((row) => {
      // A record this person sees only their OWN rows in is never pruned. The
      // buckets are the whole record's, so they cannot answer for one author's
      // half of it, and the server withholds them for exactly that reason —
      // which would leave a pruned record with rows gone and nothing to
      // replace them.
      if (row.rowScope === 'own_entries') return false;
      return isPrunable(row.id, axis.has(row.id), referenced);
    })
    .map((row) => ({ recordId: row.id, timeAxisFieldId: axis.get(row.id)! }));
};

/**
 * The records whose rows pick an entry of `recordId`: every cached schema,
 * lines records' too, with a reference field that targets it.
 */
const pickersOf = (recordId: string): string[] => {
  const rows = getDb().select({ id: recordCatalog.id }).from(recordCatalog).all();
  const schemas = withLineSchemas(rows.flatMap((row) => {
    const schema = schemaFor(row.id);
    return schema === null ? [] : [schema];
  }));
  return [...new Set(schemas.filter((schema) => schema.fields.some((field) => field.targetRecordId === recordId)).map((schema) => schema.recordId))];
};

/**
 * The titles of the rows of `recordId` about to be pruned that a row the
 * device keeps still picks (Phase 9a review of step 9.8): read from the
 * mirror before they go, so the pick is named after them. A pull sends a
 * picked entry's title only beside the row that picks it, and a row behind
 * the cursor (an order synced before the window reached its record) is never
 * sent again.
 */
const keptPickTitles = (recordId: string, pickers: readonly string[], ids: readonly string[]): { entryId: string; tenantId: string; title: string }[] => {
  const others = pickers.filter((one) => one !== recordId);
  if (others.length === 0 || ids.length === 0) return [];
  const picked = queryRaw<{ id: string }>(
    `SELECT DISTINCT t.atom AS id FROM entry_mirror m, json_tree(m.values_json) t
      WHERE m.record_id IN (${others.map(() => '?').join(', ')}) AND m.is_current = 1 AND m.deleted_at IS NULL
        AND t.atom IN (${ids.map(() => '?').join(', ')})`,
    [...others, ...ids],
  ).map((row) => row.id);
  if (picked.length === 0) return [];
  const titles = titlesForEntryIds(picked);
  const tenants = new Map(
    getDb().select({ entryId: entryMirror.entryId, tenantId: entryMirror.tenantId }).from(entryMirror).where(inArray(entryMirror.entryId, picked)).all().map((row) => [row.entryId, row.tenantId] as const),
  );
  return picked.flatMap((entryId) => {
    const title = titles[entryId];
    const tenantId = tenants.get(entryId);
    return title === undefined || tenantId === undefined || tenantId === null ? [] : [{ entryId, tenantId, title }];
  });
};

/** One record's prune: what went, and the day it is now complete from. */
export type PruneOutcome = {
  readonly recordId: string;
  /** The day the record is now complete from, or null when it holds everything again. */
  readonly floorDay: string | null;
  readonly removed: number;
};

/**
 * Bring one record down to its window, in chunks, and write the floor it now
 * holds from.
 *
 * Chunked for the reason `APPLY_ROWS_AT_ONCE` exists in the sync engine: a
 * delete on `entry_mirror` writes five indexes and a search document, and one
 * transaction over thousands of rows holds the JavaScript thread long enough
 * for a tap to answer seconds late.
 *
 * `floor_day` is written only when something lies below it: a bucketed day
 * older than the floor, whose rows are about to go or never arrived. A record
 * already inside its window holds everything it ever held, and saying otherwise
 * would send its cards to the buckets for no reason — where a breakdown, which
 * no bucket can answer, draws "Nothing yet" over rows the device holds.
 */
export const pruneRecord = async (
  record: PrunableRecord,
  today: string,
  /**
   * The day a windowed first download asked the server for, or null on a run
   * that asked for everything.
   *
   * It is half the horizon, and the half a prune cannot discover. A record
   * under the row cap whose older rows the fill never delivered has nothing
   * below its floor to delete — so without this it would delete nothing, write
   * no floor, and every all-time card would then trust a mirror holding ninety
   * days.
   */
  fillFrom: string | null,
  /** Called between chunks so the JavaScript thread is never held. */
  breathe: () => Promise<void> = async () => undefined,
): Promise<PruneOutcome | null> => {
  const recordId = record.recordId;

  /**
   * Nothing happens to a record this device holds no bucket for.
   *
   * The same interlock the delete takes, applied to the HORIZON as well. A
   * floor sends every card below it to the buckets, so a floor on a record with
   * no buckets would turn a screen full of rows into a screen full of
   * placeholders. A record whose buckets the server withheld, or whose first
   * download has not finished, is left exactly as it is.
   */
  const bucketed = queryRaw<{ one: number }>(
    'SELECT 1 AS one FROM aggregate_bucket WHERE record_id = ? LIMIT 1',
    [recordId],
  ).length > 0;
  if (!bucketed) return null;

  const asked = floorStatement(recordId, today);
  const cut = queryRaw<{ floor: string | null }>(asked.sql, asked.params)[0]?.floor ?? null;
  const floorDay = laterFloor(cut, fillFrom);
  if (floorDay === null) return null;

  /**
   * The horizon is written BEFORE anything is deleted, and that order is the
   * point.
   *
   * A prune interrupted part way — the app killed mid-chunk — would otherwise
   * leave rows gone and no floor, and every card would read a mirror that is
   * quietly short. Claiming the horizon first is safe in the other direction:
   * a card below a floor reads the buckets, and the buckets are exact whether
   * or not the rows beside them have gone yet.
   */
  const held = floorDayOf(recordId);

  /**
   * A floor with nothing below it is no floor.
   *
   * The duration floor always has a day, so without this every bucketed record
   * was marked pruned on its first run, whatever it held. The buckets are the
   * server's whole history, a day per day it holds rows, so a bucket older than
   * the floor is exactly what the floor exists to say: rows the delete below
   * will take, or rows a windowed fill never sent. No such bucket, and the
   * mirror holds every bucketed day there is — nothing to delete, and nothing
   * for a card to miss.
   *
   * A floor an earlier run wrote over nothing is taken back on the same test,
   * so a device that already carries one stops sending its cards to the
   * buckets. A floor that did cut something keeps a bucket below it for good,
   * and is never taken back.
   */
  const effective = laterFloor(held, floorDay)!;
  const below = queryRaw<{ one: number }>(
    'SELECT 1 AS one FROM aggregate_bucket WHERE record_id = ? AND day < ? LIMIT 1',
    [recordId, effective],
  ).length > 0;
  if (!below) {
    if (held === null) return null;
    getDb().update(recordCatalog).set({ floorDay: null }).where(eq(recordCatalog.id, recordId)).run();
    return { recordId, floorDay: null, removed: 0 };
  }

  const moved = laterFloor(held, floorDay) !== held;
  if (moved) {
    getDb().update(recordCatalog).set({ floorDay }).where(eq(recordCatalog.id, recordId)).run();
  }

  let removed = 0;
  const pickers = pickersOf(recordId);
  for (;;) {
    const next = doomedStatement(recordId, floorDay, `$."${record.timeAxisFieldId}"`);
    const doomed = queryRaw<{ entryId: string }>(next.sql, next.params);
    if (doomed.length === 0) break;
    const ids = doomed.map((row) => row.entryId);
    const kept = keptPickTitles(recordId, pickers, ids);
    getDb().transaction((tx) => {
      // A row a kept row still picks is named from here on by the title it had (step 9.8): the pull sends one only with the row that picks it.
      for (const one of kept) {
        tx.insert(pickTitle)
          .values({ entryId: one.entryId, tenantId: one.tenantId, recordId, title: one.title })
          .onConflictDoUpdate({ target: pickTitle.entryId, set: { tenantId: one.tenantId, recordId, title: one.title } })
          .run();
      }
      tx.delete(entryMirror).where(inArray(entryMirror.entryId, ids)).run();
      // SEAM 7 of 8: a search hit that opens nothing is worse than no hit. In
      // the same transaction as the rows, so the two can never disagree.
      forgetDocs(ids);
    });
    removed += ids.length;
    if (doomed.length < PRUNE_CHUNK) break;
    await breathe();
  }
  // An outcome means something happened. A second run over a record already at
  // its window moves no floor and takes no row, and says so by answering null.
  return removed === 0 && !moved ? null : { recordId, floorDay, removed };
};

/**
 * The whole prune for one business, after a pull that caught the device up.
 *
 * Runs only over records that may be pruned at all, and only over records this
 * device holds a bucket for: the interlock in `doomedStatement` means a record
 * with no buckets loses nothing anyway, and skipping it early saves the walk.
 */
export const pruneMirror = async (
  tenantId: string,
  today: string = utcToday(),
  fillFrom: string | null = null,
  breathe: () => Promise<void> = async () => undefined,
): Promise<PruneOutcome[]> => {
  const done: PruneOutcome[] = [];
  for (const record of prunableRecords(tenantId)) {
    const outcome = await pruneRecord(record, today, fillFrom, breathe);
    if (outcome !== null) done.push(outcome);
  }
  return done;
};

/**
 * How many rows one run must forget before the file itself is rebuilt.
 *
 * A delete frees PAGES, not disk: SQLite keeps the freed pages inside the file
 * for the next row to use. On the account this was measured against that left a
 * 980 MB file holding four thousand rows, which is storage a person paid for.
 *
 * Only a VACUUM gives it back, and a VACUUM rewrites the whole file — so it is
 * kept for the one run that earns it, the first prune after an upgrade on a
 * device that mirrored everything. A device installed after the window shipped
 * never downloads that much and never reaches this number.
 */
const VACUUM_AFTER_ROWS = 50_000;

/**
 * Give back what a prune freed: the write-ahead log first, then the file.
 *
 * Both run on the second connection, off the JavaScript thread, and both are
 * allowed to fail. A checkpoint that cannot take its lock and a vacuum that
 * cannot find room are disappointments, not faults: the rows are already gone
 * and every number on the screen is already right.
 */
export const reclaimSpace = async (removed: number): Promise<void> => {
  if (removed === 0) return;
  // The log holds every page the prune rewrote. Folding it back into the
  // database and truncating it is cheap, and on a large prune it is most of
  // the space.
  await execRawAsync('PRAGMA wal_checkpoint(TRUNCATE)').catch(() => undefined);
  if (removed < VACUUM_AFTER_ROWS) return;
  await execRawAsync('VACUUM').catch(() => undefined);
};
